// Everything that stands on the shop floor.
//
// Top half: the zone lookup (zoneAtZ / zoneStyle), the cross-aisle carve that
// shortens any bay overlapping one of the four walkways, the BANDS / BAY_PLAN /
// BAYS layout tables, FLICK_TABLES, and the shared geometry buckets — paper.*
// and glow.* are global, while each zone gets its own {wood, trim, divs} set
// through bucketsFor() so it can carry its own timber material.
//
// Bottom half: the fixture builders (spine runs, faced ranks, dig bins, the one
// rack:sleeves InstancedMesh, bays, wall shelving, flick tables, counter and
// clerk, speakers, alcoves, floor crates, framed pictures, flyer collages and
// taped signage) and buildStore(), which assembles the lot, flushes every
// bucket into a handful of merged meshes, and adds the neon fascia and the
// three poster/clutter passes.
//
// Recovered from the production bundle. Numbers and strings are byte-identical
// to the shipped source.

import { figureGeometry, figureMaterial } from './figureData.js';
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

import { ZONES, ALCOVES, ROOM_HALF_W, genreColorInt } from './zones.js';
import { ROOM } from './room.js';
import {
    LP_SIZE,
    LP_THICKNESS,
    PICKABLES,
    RACKS,
    COUNTER_SIZE,
    COUNTER_RECT,
    COUNTER_X,
    COUNTER_Z,
    COUNTER_YAW,
    SPEAKER_ANCHORS,
    speakerRig,
    SLEEVE_SIZE,
    SLEEVE_PITCH,
    SLEEVES,
    CRATE_DEPTH,
    CRATE_WALL,
    WIDE_ROOM,
    RUN_X,
    addCollider,
    part,
    partAt,
    partL,
    flushBucket,
    frameOf
} from './store.js';
import {
    labelAtlas,
    labelFit,
    atlasQuad,
    coverAtlas,
    posterAtlas,
    cardAtlas,
    genreCell,
    GENRE_CELL_CHOICES,
    assignFrontCell,
    ensureDigArt,
    sleeveArtDebug,
    flushAtlas,
    SPINE_MESHES,
    SPINE_PALETTE,
    SIGN_PHRASES,
    SIGN_TILE_INDEX,
    LABEL_TILE_INDEX,
    BLANK_LABEL_TILE,
    GENRE_HERO_RECORDS
} from './atlases.js';
import {
    bays as genreTree,
    GENRE_BY_ID as genresById,
    frontUrl,
    backUrl
} from './catalogue.js';
import {
    rnd as rand,
    woodTexture,
    spineTexture as spineStripTexture,
    POSTER_COUNT,
    CARD_COUNT
} from './textures.js';

// ---------------------------------------------------------------------------
// Zone lookup
// ---------------------------------------------------------------------------

function zoneAtZ(z) {
    return ZONES.find(zone => z >= zone.z[0] && z < zone.z[1]) || ZONES[ZONES.length - 1];
}

const ZONE_STYLE_CACHE = {};

// Decorates a zone (or alcove) definition with the spine palette plus a colour
// per genre it holds. Memoised by id.
function zoneStyle(zone) {
    if (ZONE_STYLE_CACHE[zone.id]) return ZONE_STYLE_CACHE[zone.id];
    const palette = SPINE_PALETTE.map(hex => new THREE.Color(hex));
    for (const genreId of zone.genres.slice(0, 2)) palette.push(new THREE.Color(genreColorInt(genreId)));
    const style = {
        ...zone,
        palette: palette,
        saturation: .42
    };
    ZONE_STYLE_CACHE[zone.id] = style;
    return style;
}

// ---------------------------------------------------------------------------
// The cross aisles, and the carve that keeps bays out of them
// ---------------------------------------------------------------------------

const CROSS_AISLE_Z = [4.6, 1.2, -2.2, -5.4],
    CROSS_AISLE_WIDTH = 1.4;

// Trims every planned bay back out of the four cross aisles. A bay wholly
// inside an aisle collapses to zero length; one straddling an aisle loses
// whichever end is shorter. Anything left under .55 long is dropped.
function carveCrossAisles(plan) {
    const kept = [];
    for (const bay of plan) {
        let near = bay.z - bay.len / 2,
            far = bay.z + bay.len / 2;
        for (const aisleZ of CROSS_AISLE_Z) {
            const aisleNear = aisleZ - CROSS_AISLE_WIDTH / 2,
                aisleFar = aisleZ + CROSS_AISLE_WIDTH / 2;
            if (!(far <= aisleNear || near >= aisleFar)) {
                if (near >= aisleNear && far <= aisleFar) {
                    near = far = 0;
                    break;
                }
                if (near < aisleNear && far > aisleFar) {
                    if (aisleNear - near >= far - aisleFar) far = aisleNear;
                    else near = aisleFar;
                } else if (near < aisleNear) far = aisleNear;
                else near = aisleFar;
            }
        }
        const len = far - near;
        if (!(len < .55)) kept.push({
            ...bay,
            z: (near + far) / 2,
            len: len
        });
    }
    return kept;
}

// ---------------------------------------------------------------------------
// The floor plan: five bands along Z, a genre per (run, band) slot
// ---------------------------------------------------------------------------

const BANDS = {
        A: {
            z: 6.68,
            len: 3
        },
        B: {
            z: 2.9,
            len: 2.28
        },
        C: {
            z: -.5,
            len: 2.28
        },
        D: {
            z: -3.8,
            len: 2.08
        },
        E: {
            z: -7.08,
            len: 2.22
        }
    },
    BAY_PLAN = [{
        g: "rockpsych",
        run: 0,
        band: "A"
    }, {
        g: "hiphop",
        run: 1,
        band: "A"
    }, {
        g: "rnb",
        run: 0,
        band: "B"
    }, {
        g: "disco",
        run: 1,
        band: "B"
    }, {
        g: "pop",
        run: 1,
        band: "C"
    }, {
        g: "soundtracks",
        run: 3,
        band: "C"
    }, {
        g: "latin",
        run: 0,
        band: "D"
    }, {
        g: "world",
        run: 1,
        band: "D"
    }, {
        g: "blues",
        run: 2,
        band: "D"
    }, {
        g: "classical",
        run: 1,
        band: "E"
    }, {
        g: "folk",
        run: 2,
        band: "E"
    }, {
        g: "gospel",
        run: 3,
        band: "E"
    }].map(bay => ({
        ...bay,
        z: BANDS[bay.band].z,
        len: BANDS[bay.band].len
    })),
    BAYS = carveCrossAisles(BAY_PLAN),
    FLICK_TABLES = [{
        run: 2,
        band: "A",
        sign: 0
    }, {
        run: 2,
        band: "B",
        sign: 6
    }],
    GENRE_BY_ID = {};
for (const genre of genreTree()) GENRE_BY_ID[genre.genreId] = genre;

// The subgenre window a bay shows; `subs` is a [start, end] slice.
function subgenresFor(bay) {
    const genre = GENRE_BY_ID[bay.g];
    if (!genre) return [];
    const start = bay.subs ? bay.subs[0] : 0,
        end = bay.subs ? bay.subs[1] : genre.subgenres.length;
    return genre.subgenres.slice(start, end).map(sub => ({
        ...sub,
        genreId: bay.g
    }));
}

// ---------------------------------------------------------------------------
// The shared geometry buckets
// ---------------------------------------------------------------------------

// Grain relief: each timber material uses its own albedo map as its bump map.
const WOOD_BUMP = 1.2;

const paper = {
        poster: [],
        card: [],
        cover: [],
        label: [],
        board: [],
        cord: [],
        frame: [],
        kraft: []
    },
    glow = {
        batten: [],
        warm: [],
        neonRed: [],
        neonCyan: [],
        bulb: [],
        daylight: []
    },
    paintParts = [],
    zoneBuckets = {};

// One {wood, trim, divs} bucket set per zone id, so each zone keeps its own
// timber and trim material.
function bucketsFor(zone) {
    if (zoneBuckets[zone.id]) return zoneBuckets[zone.id];
    const buckets = {
        sec: zone,
        wood: [],
        trim: [],
        divs: [],
        pal: zone.palette,
        recs: {
            length: 0
        }
    };
    zoneBuckets[zone.id] = buckets;
    return buckets;
}

function lipBucketFor(buckets) {
    const trim = buckets.sec.fixture.trim;
    return trim === "chrome" || trim === "brass" ? buckets.trim : buckets.wood;
}

// ---------------------------------------------------------------------------
// Spines
// ---------------------------------------------------------------------------

// A run of leaning record spines across `length`. Returns placement records;
// every ~16-28 spines the lean flips and a small gap opens.
function spineRun(length, thicknessScale = 1) {
    const out = [];
    let x = -length / 2,
        lean = (rand() - .5) * .3,
        sinceBreak = 0,
        breakAt = 16 + Math.floor(rand() * 13);
    for (; x < length / 2;) {
        const thickness = LP_THICKNESS * thicknessScale * (1 + .35 * rand());
        out.push({
            x: x + thickness / 2,
            t: thickness,
            lean: lean + (rand() - .5) * .05,
            dz: (rand() - .5) * .05,
            dy: -rand() * .012
        });
        x += thickness;
        lean = lean > 0 ? Math.max(0, lean - .04) : Math.min(0, lean + .04);
        if (++sinceBreak >= breakAt) {
            x += .02 + rand() * .05;
            lean = (rand() < .5 ? -1 : 1) * (.22 + rand() * .13);
            sinceBreak = 0;
            breakAt = 16 + Math.floor(rand() * 13);
        }
    }
    return out;
}

const _spineMat = new THREE.Matrix4,
    _spineQuat = new THREE.Quaternion,
    _spineEuler = new THREE.Euler,
    _spinePos = new THREE.Vector3,
    _spineScale = new THREE.Vector3,
    spineBatches = {};

function spineBatchFor(buckets) {
    const id = buckets.sec.id;
    if (!spineBatches[id]) spineBatches[id] = {
        m: [],
        g: [],
        seed: Math.abs(buckets.sec.id.charCodeAt(0) * 7 + buckets.sec.id.length * 29)
    };
    return spineBatches[id];
}

// One instance of the shared spine box, scaled to `thickness` on x and
// `depthScale` on z, leaned by `lean` about z, placed inside `frame`.
function addSpine(buckets, frame, x, y, z, lean, thickness, depthScale, genreId) {
    _spinePos.set(x, y, z);
    _spineEuler.set(0, 0, lean);
    _spineQuat.setFromEuler(_spineEuler);
    _spineScale.set(thickness / LP_THICKNESS, 1, depthScale);
    _spineMat.compose(_spinePos, _spineQuat, _spineScale);
    const batch = spineBatchFor(buckets);
    batch.m.push(new THREE.Matrix4().multiplyMatrices(frame, _spineMat));
    batch.g.push(genreId || buckets.sec.genres[0]);
}

// One InstancedMesh per zone batch, each with its own procedural spine strip.
function buildSpineInstances(parent) {
    const geometry = new THREE.BoxGeometry(LP_THICKNESS, LP_SIZE, LP_SIZE),
        color = new THREE.Color,
        palette = SPINE_PALETTE.map(hex => new THREE.Color(hex));
    for (const batch of Object.values(spineBatches)) {
        if (!batch.m.length) continue;
        const mesh = new THREE.InstancedMesh(geometry, new THREE.MeshStandardMaterial({
            map: spineStripTexture(batch.seed),
            roughness: .93,
            metalness: 0
        }), batch.m.length);
        mesh.name = "spines:" + batch.seed;
        mesh.castShadow = false;
        mesh.receiveShadow = true;
        batch.m.forEach((matrix, index) => {
            mesh.setMatrixAt(index, matrix);
            color.copy(palette[index % palette.length]);
            const hsl = {
                h: 0,
                s: 0,
                l: 0
            };
            color.getHSL(hsl);
            color.setHSL(hsl.h, hsl.s * .8, .12 + index * 17 % 46 / 130);
            mesh.setColorAt(index, color);
        });
        mesh.instanceMatrix.needsUpdate = true;
        mesh.instanceColor.needsUpdate = true;
        mesh.userData.genres = batch.g;
        SPINE_MESHES.push(mesh);
        parent.add(mesh);
    }
}

// ---------------------------------------------------------------------------
// Faced records, shelf lips, paper dressing
// ---------------------------------------------------------------------------

// A row of front-facing sleeves leaning back at `pitch` along a shelf. Each
// gets a blank label quad on its back face. `whiteLabel` shows plain label
// stock for two out of every three.
function facedRank(buckets, frame, opts) {
    const {
        lx0: lx0,
        lx1: lx1,
        ly: ly,
        lz: lz,
        pitch: pitch,
        face: face = 1,
        spacing: spacing = .115,
        size: size = LP_SIZE,
        jitter: jitter = .06,
        genreId: genreIdOpt,
        whiteLabel: whiteLabel = false
    } = opts, covers = coverAtlas(), count = Math.max(1, Math.floor((lx1 - lx0) / spacing)), frameYaw = new THREE.Euler().setFromRotationMatrix(frame, "YXZ").y, frameOrigin = new THREE.Vector3().setFromMatrixPosition(frame), genreId = genreIdOpt || buckets.sec.genres[0];
    for (let i = 0; i < count; i++) {
        const x = lx0 + (i + .5) * ((lx1 - lx0) / count),
            blank = whiteLabel && i % 3 !== 1,
            quad = blank ? atlasQuad(labelAtlas(), BLANK_LABEL_TILE, size, size) : atlasQuad(covers, genreCell(genreId, Math.floor(rand() * GENRE_CELL_CHOICES)), size, size);
        if (!blank) {
            const shade = .86 + rand() * .14,
                vertexCount = quad.attributes.position.count,
                colors = new Float32Array(vertexCount * 3).fill(shade);
            quad.setAttribute("color", new THREE.BufferAttribute(colors, 3));
        }
        const position = new THREE.Vector3(x, ly + (rand() - .5) * .008, lz + (rand() - .5) * jitter);
        position.applyAxisAngle(new THREE.Vector3(0, 1, 0), frameYaw).add(frameOrigin);
        const euler = new THREE.Euler(0, frameYaw + (face > 0 ? 0 : Math.PI), 0, "YXZ");
        euler.x = -pitch * face;
        euler.z = (rand() - .5) * .09;
        const quaternion = new THREE.Quaternion().setFromEuler(euler);
        partAt(blank ? paper.label : paper.cover, quad, position, quaternion);
        const back = atlasQuad(labelAtlas(), BLANK_LABEL_TILE, size, size),
            backQuaternion = new THREE.Quaternion().setFromEuler(new THREE.Euler(euler.x, euler.y + Math.PI, -euler.z, "YXZ"));
        partAt(paper.label, back, position.clone().addScaledVector(new THREE.Vector3(0, 0, 1).applyQuaternion(quaternion), -.0016), backQuaternion);
    }
}

// The emissive strip plus the timber fascia and end caps that hide it.
// `materials` is unused here; it is passed through by the callers.
function shelfLip(group, frame, opts, materials, warm = true) {
    const {
        lx: lx,
        ly: ly,
        lz: lz,
        len: len,
        face: face = 1,
        lip: lipOpt
    } = opts, lip = lipOpt || group.userData.lipBucket, strip = new THREE.BoxGeometry(len - .1, .016, .026);
    partL(warm ? glow.warm : glow.batten, frame, strip, lx, ly - .004, lz);
    partL(lip, frame, new THREE.BoxGeometry(len, .03, .014), lx, ly + .02, lz - face * .016);
    partL(lip, frame, new THREE.BoxGeometry(len, .012, .046), lx, ly + .026, lz);
    for (const side of [-1, 1]) partL(lip, frame, new THREE.BoxGeometry(.014, .04, .046), lx + side * (len / 2 - .007), ly + .008, lz);
    const fascia = new THREE.BoxGeometry(len + .04, .055, .016);
    partL(lip, frame, fascia, lx, ly + .006, lz + face * .028);
}

// Overlapping gig posters plus a scatter of index cards, layered forward along
// `face` so they do not z-fight. `face` is the outward normal in frame-local z.
function flyerCollage(frame, x0, x1, y0, y1, z, face = 1, density = 1) {
    const posters = posterAtlas(),
        ry = face > 0 ? 0 : Math.PI,
        count = Math.max(6, Math.round((x1 - x0) * (y1 - y0) * 110 * density)),
        layerStep = 12e-5;
    let layer = 0;
    for (let i = 0; i < count; i++) {
        const height = .15 + rand() * .2,
            quad = atlasQuad(posters, Math.floor(rand() * POSTER_COUNT), height * .7, height);
        partL(paper.poster, frame, quad, x0 + rand() * (x1 - x0), y0 + rand() * (y1 - y0), z + face * (6e-4 + layer++ * layerStep), 0, ry, (rand() - .5) * .75);
    }
    for (let i = 0; i < Math.max(1, count / 6 * density); i++) {
        const quad = atlasQuad(cardAtlas(), Math.floor(rand() * CARD_COUNT), .13, .09);
        partL(paper.card, frame, quad, x0 + rand() * (x1 - x0), y0 + rand() * (y1 - y0), z + face * (6e-4 + layer++ * layerStep), 0, ry, (rand() - .5) * .9);
    }
}

// One lettered sign from SIGN_PHRASES, width-fitted to its text.
function shopSign(frame, x, y, z, ry, width = .26, phrase = null, tilt = .05) {
    const index = phrase == null ? Math.floor(rand() * SIGN_PHRASES.length) : phrase % SIGN_PHRASES.length,
        tile = SIGN_TILE_INDEX[SIGN_PHRASES[index]],
        quadWidth = width * labelFit(tile),
        quad = atlasQuad(labelAtlas(), tile, quadWidth, width * .375);
    partL(paper.label, frame, quad, x, y, z, 0, ry, (rand() - .5) * tilt * 2);
}

// An index card with one or two bits of tape across the top.
function tapedFlyer(frame, x, y, z, ry, width = .075) {
    const height = width * (.62 + rand() * .35),
        tilt = (rand() - .5) * .55,
        sinRy = Math.sin(ry),
        cosRy = Math.cos(ry);
    partL(paper.card, frame, atlasQuad(cardAtlas(), Math.floor(rand() * CARD_COUNT), width, height), x, y, z, 0, ry, tilt);
    const tapeCount = rand() < .45 ? 2 : 1;
    for (let i = 0; i < tapeCount; i++) {
        const side = tapeCount === 1 ? (rand() < .5 ? -1 : 1) : (i ? 1 : -1);
        partL(paper.kraft, frame, new THREE.BoxGeometry(width * .34, height * .22, .002), x + side * width * .32 * Math.cos(ry) - sinRy * .0015, y + height / 2 - height * .06, z - side * width * .32 * Math.sin(ry) - cosRy * .0015, 0, ry, tilt + side * .5);
    }
}

// ---------------------------------------------------------------------------
// Cassettes
// ---------------------------------------------------------------------------

const cassetteMatrices = [];

// Rows of cassette cases along a shelf. Matrices only; the InstancedMesh is
// built in buildStore().
function cassetteRows(frame, x0, x1, y, rows, z) {
    const columns = Math.floor((x1 - x0) / .112);
    for (let row = 0; row < rows; row++)
        for (let column = 0; column < columns; column++) {
            if (rand() < .06) continue;
            const local = new THREE.Matrix4().compose(new THREE.Vector3(x0 + (column + .5) * .112, y + row * .076, z), new THREE.Quaternion().setFromEuler(new THREE.Euler(0, 0, (rand() - .5) * .05)), new THREE.Vector3(1, 1, 1));
            cassetteMatrices.push(new THREE.Matrix4().multiplyMatrices(frame, local));
        }
}

// ---------------------------------------------------------------------------
// Dig bins — the diggable racks along the top of a bay
// ---------------------------------------------------------------------------

// Splits the bay length between its subgenres, and for each builds a rack
// record (registered in RACKS), up to 12 sleeve Object3Ds registered in
// SLEEVES, a divider board, a lettered label, placards, taped flyers and a
// run of spines behind.
function buildDigBins(buckets, frame, opts) {
    const {
        subs: subs,
        len: len,
        W: width,
        TOP: top,
        face: face,
        gid: genreId,
        head: head = true
    } = opts;
    if (!subs.length) return;
    const usable = len - .04,
        slot = usable / subs.length,
        frameQuat = new THREE.Quaternion().setFromRotationMatrix(frame),
        frameOrigin = new THREE.Vector3().setFromMatrixPosition(frame),
        toWorld = (x, y, z) => new THREE.Vector3(x, y, z).applyQuaternion(frameQuat).add(frameOrigin),
        faceDir = new THREE.Vector3(0, 0, face).applyQuaternion(frameQuat).normalize(),
        ry = face > 0 ? 0 : Math.PI,
        sleeveQuat = new THREE.Quaternion().setFromEuler(new THREE.Euler(-.14, ry, 0, "YXZ")).premultiply(frameQuat),
        binZ = face * (width / 2 - .075),
        binY = top + .085,
        genreLabel = genresById[genreId]?.label || genreId;
    subs.forEach((sub, subIndex) => {
        const slotX = -usable / 2 + (subIndex + .5) * slot,
            records = (sub.records || []).slice(0, 12);
        if (!records.length) return;
        const rack = {
            position: toWorld(slotX, binY, binZ),
            faceDir: faceDir.clone(),
            stand: toWorld(slotX, 0, binZ).addScaledVector(faceDir, .62).setY(0),
            records: records,
            sleeves: [],
            genreId: genreId,
            subgenreId: sub.id,
            label: sub.label,
            genreLabel: genreLabel,
            binTop: top,
            frontCell: -1,
            digSlot: null
        };
        assignFrontCell(rack);
        rack.ensureArt = () => ensureDigArt(rack);
        rack.art = index => sleeveArtDebug(rack.sleeves[index]);
        records.forEach((record, index) => {
            const restPosition = toWorld(slotX, binY - index * .0015, binZ - face * (SLEEVE_PITCH * index)),
                sleeve = new THREE.Object3D;
            sleeve.position.copy(restPosition);
            sleeve.quaternion.copy(sleeveQuat);
            sleeve.matrixAutoUpdate = false;
            sleeve.updateMatrix();
            sleeve.userData = {
                rest: {
                    position: restPosition.clone(),
                    quaternion: sleeveQuat.clone()
                },
                rack: rack,
                indexInRack: index,
                instIndex: SLEEVES.dummy.length,
                record: record,
                title: `${record.artist} — ${record.title}`,
                artist: record.artist,
                album: record.title,
                year: record.year,
                label: record.label,
                genre: genreLabel,
                subgenre: sub.label,
                tracks: record.tracks,
                frontUrl: frontUrl(record),
                backUrl: backUrl(record)
            };
            SLEEVES.dummy.push(sleeve);
            SLEEVES.tile.push(index === 0 && rack.frontCell >= 0 ? rack.frontCell : genreCell(genreId, subIndex * 3 + index * 5));
            rack.sleeves.push(sleeve);
        });
        const dividerZ = face * Math.max(.02, width / 2 - .075 - (SLEEVE_PITCH * records.length + .016));
        partL(paper.board, frame, new THREE.BoxGeometry(slot - .024, .48, .006), slotX, top + .1, dividerZ, 0, 0, 0);
        const labelWidth = Math.min(.255, slot - .032);
        partL(paper.label, frame, atlasQuad(labelAtlas(), LABEL_TILE_INDEX[genreId + "/" + sub.id] ?? 0, labelWidth * labelFit(LABEL_TILE_INDEX[genreId + "/" + sub.id] ?? 0), labelWidth / 2.66), slotX, top + .268, dividerZ + face * .005, 0, ry, (rand() - .5) * .05);
        const frontZ = face * (width / 2 + .036);
        if (subIndex % 6 === 3) tapedPlacard(frame, SIGN_TILE_INDEX["STAFF PICKS"] ?? 0, slotX, top - .115, frontZ, ry, Math.min(.185, slot - .05), .09);
        else if (rand() < .35) shopSign(frame, slotX + (rand() - .5) * .05, top - .125, frontZ, ry, Math.min(.17, slot - .06), (subIndex * 5 + 1) % SIGN_PHRASES.length, .1);
        for (let i = 0, n = 1 + Math.floor(rand() * 3); i < n; i++) tapedFlyer(frame, slotX + (rand() - .5) * (slot - .1), top - .28 - rand() * .16, frontZ, ry, .055 + rand() * .035);
        if (subIndex % 3 === 1) {
            const stackZ = binZ - face * (SLEEVE_PITCH * 12 + .13),
                stackCount = 3 + Math.floor(rand() * 3);
            for (let i = 0; i < stackCount; i++) partL(paper.kraft, frame, new THREE.BoxGeometry(LP_SIZE * .96, .0085, LP_SIZE * .96), slotX + (rand() - .5) * .03, top - .085 + i * .0095, stackZ + (rand() - .5) * .02, 0, (rand() - .5) * .16, 0);
        }
        RACKS.push(rack);
    });
    const spineZ = face * Math.max(.015, width / 2 - .075 - (SLEEVE_PITCH * 12 + .085));
    for (let row = 0; row < 3; row++) {
        const rowZ = spineZ - face * row * .052;
        for (const spine of spineRun(usable - .04, 1)) addSpine(buckets, frame, spine.x, top - .2 + spine.dy, rowZ + spine.dz * .2, spine.lean, spine.t, width / LP_SIZE * .34, genreId);
    }
    if (!head) return;
    const headZ = -face * (width / 2 + .012),
        headY = top + .55;
    for (const side of [-1, 1]) partL(buckets.wood, frame, new THREE.BoxGeometry(.024, headY - top - .075, .024), side * .19, top + (headY - top - .075) / 2, headZ);
    partL(paper.board, frame, new THREE.BoxGeometry(.5, .19, .008), 0, headY, headZ, 0, 0, 0);
    partL(paper.label, frame, atlasQuad(labelAtlas(), LABEL_TILE_INDEX[genreId] ?? 0, .465 * labelFit(LABEL_TILE_INDEX[genreId] ?? 0), .175), 0, headY, headZ + face * .006, 0, ry, (rand() - .5) * .03);
}

// ---------------------------------------------------------------------------
// The one rack:sleeves InstancedMesh
// ---------------------------------------------------------------------------

// Every diggable sleeve in the shop as a single InstancedMesh sampling the
// cover atlas per instance. `aTile` is an instanced vec2 of atlas cell origins
// folded into vMapUv after the standard <uv_vertex> chunk. onBeforeRender
// re-uploads only the matrices that actually moved.
function buildSleeveInstances(parent) {
    const count = SLEEVES.dummy.length;
    if (!count) return;
    const atlas = coverAtlas(),
        geometry = new THREE.BoxGeometry(SLEEVE_SIZE, SLEEVE_SIZE, .011),
        tiles = new Float32Array(count * 2);
    for (let i = 0; i < count; i++) {
        const tile = SLEEVES.tile[i],
            column = tile % atlas.cols,
            row = Math.floor(tile / atlas.cols) % atlas.rows;
        tiles[i * 2] = column / atlas.cols;
        tiles[i * 2 + 1] = (atlas.rows - row - 1) / atlas.rows;
    }
    SLEEVES.tiles = tiles;
    geometry.setAttribute("aTile", new THREE.InstancedBufferAttribute(tiles, 2));
    const material = new THREE.MeshStandardMaterial({
            name: "rack:sleeves",
            map: atlas.tex,
            roughness: .86,
            metalness: 0
        }),
        cellW = 1 / atlas.cols,
        cellH = 1 / atlas.rows;
    material.onBeforeCompile = shader => {
        shader.vertexShader = `attribute vec2 aTile;
` + shader.vertexShader.replace("#include <uv_vertex>", `#include <uv_vertex>
	vMapUv = vMapUv * vec2(${cellW}, ${cellH}) + aTile;`);
    };
    material.customProgramCacheKey = () => "rackTile";
    const mesh = new THREE.InstancedMesh(geometry, material, count);
    mesh.name = "rack:sleeves";
    mesh.castShadow = false;
    mesh.receiveShadow = true;
    const color = new THREE.Color,
        last = new Float32Array(count * 16);
    for (let i = 0; i < count; i++) {
        mesh.setMatrixAt(i, SLEEVES.dummy[i].matrix);
        SLEEVES.dummy[i].matrix.toArray(last, i * 16);
        const shade = .86 + i * 37 % 15 / 100;
        color.setRGB(shade, shade, shade);
        mesh.setColorAt(i, color);
    }
    mesh.instanceMatrix.needsUpdate = true;
    mesh.instanceColor.needsUpdate = true;
    mesh.frustumCulled = false;
    SLEEVES.mesh = mesh;
    SLEEVES.last = last;
    mesh.onBeforeRender = () => {
        const digging = typeof window < "u" && window.__store && window.__store.digging;
        let activeRack = digging && digging.rack || null;
        if (activeRack && activeRack.raw) activeRack = activeRack.raw;
        if (activeRack !== SLEEVES.active) {
            SLEEVES.active = activeRack;
            if (activeRack) ensureDigArt(activeRack);
        }
        flushAtlas();
        let dirty = false;
        for (let i = 0; i < count; i++) {
            const sleeve = SLEEVES.dummy[i];
            sleeve.updateMatrix();
            const elements = sleeve.matrix.elements,
                base = i * 16;
            let same = true;
            for (let k = 0; k < 16; k++)
                if (last[base + k] !== elements[k]) {
                    same = false;
                    break;
                }
            if (!same) {
                if (!digging) {
                    const rack = sleeve.userData && sleeve.userData.rack;
                    if (rack && rack !== SLEEVES.active) {
                        SLEEVES.active = rack;
                        ensureDigArt(rack);
                    }
                }
                for (let k = 0; k < 16; k++) last[base + k] = elements[k];
                mesh.setMatrixAt(i, sleeve.matrix);
                dirty = true;
            }
        }
        if (dirty) mesh.instanceMatrix.needsUpdate = true;
    };
    parent.add(mesh);
}

// ---------------------------------------------------------------------------
// A bay: the freestanding fixture the dig bins sit on top of
// ---------------------------------------------------------------------------

function buildBay(group, zone, x, z, length, yaw, materials, face, opts = {}) {
    const buckets = bucketsFor(zone),
        frame = frameOf(x, z, yaw),
        kind = opts.kind ?? zone.fixture.kind,
        timber = opts.timber ?? zone.fixture.timber,
        bay = opts.seg || null,
        genreId = bay ? bay.g : zone.genres[0],
        subs = bay ? subgenresFor(bay) : [],
        whiteLabel = genreId === "electronic",
        depth = opts.depth ?? (kind === "crate-12" ? .42 : CRATE_DEPTH),
        top = kind === "browser-low" ? .86 : kind === "crate-12" ? .95 : kind === "shelf-deep" ? 1.12 : 1.05,
        wood = buckets.wood,
        trim = buckets.trim,
        bodyHeight = top - .1,
        panel = .028;
    for (const side of [1, -1]) partL(wood, frame, new THREE.BoxGeometry(length, bodyHeight, panel), 0, .1 + bodyHeight / 2, side * depth / 2);
    for (const side of [-1, 1]) partL(wood, frame, new THREE.BoxGeometry(panel, bodyHeight, depth), side * (length / 2), .1 + bodyHeight / 2, 0);
    partL(wood, frame, new THREE.BoxGeometry(length - CRATE_WALL * 2, .1, depth - CRATE_WALL * 2), 0, .05, 0);
    if (timber === "blackened" || timber === "painted-black") {
        for (const [bandY, bandHeight] of [
                [.3, .13],
                [.52, .13],
                [.74, .13]
            ])
            if (!(bandY + bandHeight / 2 > top - .06)) partL(wood, frame, new THREE.BoxGeometry(length + .02, bandHeight, .016), 0, bandY, face * (depth / 2 + .012));
        for (const side of [-1, 1]) partL(wood, frame, new THREE.BoxGeometry(.05, bodyHeight, .05), side * (length / 2 - .02), .1 + bodyHeight / 2, face * (depth / 2 - .01));
    }
    if (kind === "shelf-deep") {
        partL(wood, frame, new THREE.BoxGeometry(length, .05, .022), 0, .62, face * (depth / 2 + .011));
        partL(wood, frame, new THREE.BoxGeometry(length + .04, .05, .03), 0, .125, face * (depth / 2 + .012));
    }
    const capBucket = zone.fixture.trim === "chrome" || zone.fixture.trim === "brass" ? trim : wood;
    for (const side of [1, -1]) partL(capBucket, frame, new THREE.BoxGeometry(length + .03, .032, .07), 0, top, side * (depth / 2 + .005));
    for (const side of [-1, 1]) partL(capBucket, frame, new THREE.BoxGeometry(.07, .032, depth + .03), side * length / 2, top, 0);
    if (zone.fixture.trim === "chrome") {
        partL(trim, frame, new THREE.BoxGeometry(length - CRATE_WALL * 2 + .01, .035, .012), 0, .075, face * (depth / 2 - CRATE_WALL));
        partL(trim, frame, new THREE.BoxGeometry(length - .06, .018, .03), 0, .352, face * (depth / 2 - .06));
        for (const side of [-1, 1]) partL(trim, frame, new THREE.BoxGeometry(.03, bodyHeight * .9, .03), side * (length / 2 - .015), .1 + bodyHeight / 2, face * (depth / 2 - .015));
    }
    if (kind === "shelf-deep") partL(wood, frame, new THREE.BoxGeometry(length - .05, bodyHeight - .06, .018), 0, .1 + bodyHeight / 2, depth / 2 - .02);
    else {
        partL(wood, frame, new THREE.BoxGeometry(length - .06, .02, depth - .09), 0, .34, 0);
        const stackCount = 3 + Math.floor(rand() * 3);
        for (let i = 0; i < stackCount; i++) {
            const stackHeight = .03 + rand() * .08;
            partL(wood, frame, new THREE.BoxGeometry(LP_SIZE, stackHeight, LP_SIZE), (rand() - .5) * (length - LP_SIZE - .1), .35 + stackHeight / 2, (rand() - .5) * Math.max(.02, depth - LP_SIZE - .08), 0, (rand() - .5) * .5, 0);
        }
    }
    const twoSided = !!opts.twoSided && subs.length > 1,
        split = twoSided ? Math.ceil(subs.length / 2) : subs.length;
    buildDigBins(buckets, frame, {
        subs: subs.slice(0, split),
        len: length,
        W: depth,
        TOP: top,
        face: face,
        gid: genreId,
        head: !twoSided
    });
    if (twoSided) {
        buildDigBins(buckets, frame, {
            subs: subs.slice(split),
            len: length,
            W: depth,
            TOP: top,
            face: -face,
            gid: genreId,
            head: false
        });
        const headY = top + .55;
        for (const side of [-1, 1]) partL(buckets.wood, frame, new THREE.BoxGeometry(.026, headY - top - .075, .026), side * .19, top + (headY - top - .075) / 2, 0);
        partL(paper.board, frame, new THREE.BoxGeometry(.5, .19, .014), 0, headY, 0);
        const tile = LABEL_TILE_INDEX[genreId] ?? 0;
        for (const side of [1, -1]) partL(paper.label, frame, atlasQuad(labelAtlas(), tile, .465 * labelFit(tile), .175), 0, headY, side * .009, 0, side > 0 ? 0 : Math.PI, 0);
    }
    // Bare expression statement, preserved from the bundle.
    opts.stacked;
    const minShelfY = top + .55 + .095 + .3,
        shelfYs = (kind === "browser-low" ? [] : kind === "crate-12" ? [1.88] : kind === "shelf-deep" ? [1.72] : timber === "painted-black" ? [1.86] : [1.9]).map(y => Math.max(y, minShelfY));
    if (opts.posts)
        for (const side of [-1, 1]) {
            partL(wood, frame, new THREE.BoxGeometry(.12, ROOM.h, .12), side * (length / 2 - .06), ROOM.h / 2, face * (depth / 2 - .06));
            shopSign(frame, side * (length / 2 - .06), 1.62, face * (depth / 2), face > 0 ? 0 : Math.PI, .17);
        }
    const againstWallRun = Math.abs(opts.runX ?? 0) > 1.5;
    for (const shelfY of shelfYs)
        for (const shelfFace of twoSided ? [face, -face] : [face]) {
            if (againstWallRun) {
                shelfLip(group, frame, {
                    lx: 0,
                    ly: shelfY - .046,
                    lz: shelfFace * (depth / 2 + .02) + shelfFace * .088,
                    len: length - .2,
                    face: shelfFace,
                    color: zone.light.main.color,
                    lip: lipBucketFor(buckets)
                }, materials, true);
                continue;
            }
            const shelfZ = shelfFace * (depth / 2 + .02);
            partL(wood, frame, new THREE.BoxGeometry(length - .08, .022, .16), 0, shelfY, shelfZ, -shelfFace * .26, 0, 0);
            partL(wood, frame, new THREE.BoxGeometry(length - .08, .05, .018), 0, shelfY - .012, shelfZ + shelfFace * .082);
            facedRank(buckets, frame, {
                lx0: -length / 2 + .08,
                lx1: length / 2 - .08,
                ly: shelfY + LP_SIZE / 2 * Math.cos(.26) + .02,
                lz: shelfZ - shelfFace * (LP_SIZE / 2) * Math.sin(.26) + shelfFace * .012,
                pitch: .26,
                face: shelfFace,
                spacing: .118,
                jitter: .012,
                genreId: genreId,
                whiteLabel: whiteLabel
            });
            shelfLip(group, frame, {
                lx: 0,
                ly: shelfY - .046,
                lz: shelfZ + shelfFace * .088,
                len: length - .2,
                face: shelfFace,
                color: zone.light.main.color,
                lip: lipBucketFor(buckets)
            }, materials, true);
        }
    if (bay && length > 1)
        for (const placardFace of twoSided ? [face, -face] : [face]) tapedPlacard(frame, LABEL_TILE_INDEX[genreId] ?? 0, -length / 2 + .34, top - .21, placardFace * (depth / 2 + .014), placardFace > 0 ? 0 : Math.PI, .27, .05);
    if (kind === "crate" || kind === "crate-12") {
        const crateCount = rand() < .4 ? 1 : 0;
        for (let i = 0; i < crateCount; i++) {
            const crateX = -length / 2 + (i + .7) * (length / (crateCount + .5)),
                crateZ = face * (depth / 2 + .1);
            partL(wood, frame, new THREE.BoxGeometry(.36, .32, .2), crateX, .16, crateZ, 0, (rand() - .5) * .2, 0);
            for (const spine of spineRun(.28)) addSpine(buckets, frame, crateX + spine.x, .29 + spine.dy, crateZ + spine.dz * .3, spine.lean + .06, spine.t, .62, genreId);
            shopSign(frame, crateX, .24, crateZ + face * .11, face > 0 ? 0 : Math.PI, .19, i % 2 ? 11 : 2);
        }
    }
    if (genreId === "rockpsych") flyerCollage(frame, -length / 2 + .02, length / 2 - .02, .26, top - .1, face * (depth / 2 + .03), face, .55);
    else if (kind !== "shelf-deep" && rand() < .75) flyerCollage(frame, -length / 2 + .1, length / 2 - .1, .34, top - .16, face * (depth / 2 + .03), face, .2);
    if (genreId === "hiphop" && kind === "crate-12") cassetteRows(frame, -length / 2 + .05, length / 2 - .05, .3, 4, face * (depth / 2 + .012));
    for (const side of [-1, 1]) shopSign(frame, side * (length / 2 + .022), top - .3, (rand() - .5) * .1, side * Math.PI / 2, .26);
    const cosYaw = Math.abs(Math.cos(yaw)),
        sinYaw = Math.abs(Math.sin(yaw));
    addCollider(x, z, length * cosYaw + depth * sinYaw, length * sinYaw + depth * cosYaw);
    return {
        TOP: top,
        W: depth,
        frame: frame
    };
}

// ---------------------------------------------------------------------------
// Wall shelving above a wall-side bin run
// ---------------------------------------------------------------------------

function buildWallShelf(group, zone, side, z, length, baseY, shelfCount, materials, facedTop = false, bay = null) {
    const buckets = bucketsFor(zone),
        genreId = bay ? bay.g : zone.genres[0],
        wallX = side * (ROOM.w / 2 - .17),
        yaw = side > 0 ? -Math.PI / 2 : Math.PI / 2,
        frame = frameOf(wallX, 0, yaw),
        lx = side > 0 ? z : -z,
        deep = zone.fixture.kind === "shelf-deep",
        height = 2.55 - baseY,
        depth = deep ? .4 : .33,
        wood = buckets.wood;
    partL(wood, frame, new THREE.BoxGeometry(length, height, .02), lx, baseY + height / 2, -depth / 2);
    for (const end of [-1, 1]) partL(wood, frame, new THREE.BoxGeometry(.025, height, depth), lx + end * length / 2, baseY + height / 2, 0);
    const bayCount = Math.max(1, Math.round(length / .82)),
        bayWidth = length / bayCount;
    for (let i = 1; i < bayCount; i++) partL(wood, frame, new THREE.BoxGeometry(.022, height, depth), lx - length / 2 + i * bayWidth, baseY + height / 2, 0);
    const shelfStep = (height - .08) / shelfCount;
    for (let shelf = 0; shelf < shelfCount; shelf++) {
        const shelfY = baseY + .04 + shelf * shelfStep,
            isTop = shelf === shelfCount - 1;
        if (facedTop && shelf >= shelfCount - 2) {
            partL(wood, frame, new THREE.BoxGeometry(length, .022, .16), lx, shelfY, depth / 2 - .1, .21, 0, 0);
            facedRank(buckets, frame, {
                lx0: lx - length / 2 + .06,
                lx1: lx + length / 2 - .06,
                ly: shelfY + LP_SIZE / 2 * Math.cos(.21) + .02,
                lz: depth / 2 - .1 - LP_SIZE / 2 * Math.sin(.21),
                pitch: .21,
                face: 1,
                spacing: .125,
                jitter: .008,
                genreId: genreId,
                whiteLabel: genreId === "electronic"
            });
            if (isTop) shelfLip(group, frame, {
                lx: lx,
                ly: shelfY - .05,
                lz: depth / 2 - .02,
                len: length - .1,
                face: 1,
                color: zone.light.main.color,
                lip: lipBucketFor(buckets)
            }, materials, true);
            continue;
        }
        const highLip = shelfY > 1.92 ? .06 : 0;
        partL(wood, frame, new THREE.BoxGeometry(length, .022, depth - highLip), lx, shelfY, -highLip / 2);
        if (highLip) partL(wood, frame, new THREE.BoxGeometry(length, .075, .02), lx, shelfY + .055, depth / 2 - .02);
        if (genreId === "hiphop" && shelf < 2) {
            for (let i = 0; i < bayCount; i++) {
                const centre = lx - length / 2 + (i + .5) * bayWidth;
                cassetteRows(frame, centre - bayWidth / 2 + .05, centre + bayWidth / 2 - .05, shelfY + .055, 3, depth / 2 - .1 - highLip / 2);
            }
            continue;
        }
        for (let i = 0; i < bayCount; i++) {
            const centre = lx - length / 2 + (i + .5) * bayWidth;
            for (const spine of spineRun(bayWidth - .07, highLip ? 2 : 1.55)) addSpine(buckets, frame, centre + spine.x, shelfY + LP_SIZE / 2 + .012 + spine.dy, spine.dz * .25 - highLip / 2, spine.lean, spine.t, .92, genreId);
            if (shelf === 0 || shelf === 1 && rand() < .5) partL(paper.label, frame, atlasQuad(labelAtlas(), LABEL_TILE_INDEX[genreId] ?? 0, Math.min(.3, bayWidth * .45), Math.min(.3, bayWidth * .45) * .36), centre, shelfY - .055, depth / 2 - .012, 0, 0, (rand() - .5) * .05);
        }
    }
    addCollider(wallX, z, .4, length, .03);
}

// ---------------------------------------------------------------------------
// Signage
// ---------------------------------------------------------------------------

// A lettered card on a board, with a strip of tape at each top corner.
function tapedPlacard(frame, tile, x, y, z, ry, width = .24, tilt = .07) {
    const height = width * .36,
        lean = (rand() - .5) * tilt * 2,
        sinRy = Math.sin(ry),
        cosRy = Math.cos(ry);
    partL(paper.board, frame, new THREE.BoxGeometry(width + .018, height + .016, .007), x, y, z, 0, ry, lean);
    partL(paper.label, frame, atlasQuad(labelAtlas(), tile, width, height), x + sinRy * .006, y, z + cosRy * .006, 0, ry, lean);
    for (const side of [-1, 1]) partL(paper.kraft, frame, new THREE.BoxGeometry(.036, .026, .003), x + side * width * .34 * Math.cos(ry) + sinRy * .008, y + height / 2 - .004, z - side * width * .34 * Math.sin(ry) + cosRy * .008, 0, ry, lean + side * .55);
}

// ---------------------------------------------------------------------------
// Flick table — a low double-sided browsing table
// ---------------------------------------------------------------------------

function buildFlickTable(zone, x, z, signIndex) {
    const buckets = bucketsFor(zone),
        frame = frameOf(x, z, Math.PI / 2),
        length = 1.55,
        width = .62,
        height = .8;
    partL(buckets.trim, frame, new THREE.BoxGeometry(length, .05, width), 0, height, 0);
    partL(buckets.wood, frame, new THREE.BoxGeometry(length - .1, .03, width - .1), 0, .34, 0);
    for (const endX of [-1, 1])
        for (const endZ of [-1, 1]) partL(buckets.wood, frame, new THREE.BoxGeometry(.06, height, .06), endX * (length / 2 - .06), height / 2, endZ * (width / 2 - .06));
    for (const side of [-1, 1]) {
        partL(buckets.wood, frame, new THREE.BoxGeometry(length, .09, .02), 0, height + .07, side * (width / 2 - .04));
        facedRank(buckets, frame, {
            lx0: -length / 2 + .12,
            lx1: length / 2 - .12,
            ly: height + .03 + LP_SIZE / 2 * Math.cos(.3),
            lz: side * (width / 2 - .1),
            pitch: .3,
            face: side,
            spacing: .2,
            jitter: .01,
            genreId: zone.genres[0]
        });
        for (let i = 0; i < 4; i++) partL(buckets.wood, frame, new THREE.BoxGeometry(LP_SIZE, .009, LP_SIZE), side * .42, .36 + i * .011, 0, 0, (rand() - .5) * .3, 0);
    }
    for (const side of [-1, 1]) partL(buckets.wood, frame, new THREE.BoxGeometry(.018, .2, .018), side * .11, height + .11, 0);
    partL(paper.board, frame, new THREE.BoxGeometry(.3, .12, .007), 0, height + .24, 0);
    for (const side of [-1, 1]) partL(paper.label, frame, atlasQuad(labelAtlas(), SIGN_TILE_INDEX[SIGN_PHRASES[signIndex % SIGN_PHRASES.length]], .28, .105), 0, height + .24, side * .005, 0, side > 0 ? 0 : Math.PI, (rand() - .5) * .05);
    addCollider(x, z, width, length);
}

// ---------------------------------------------------------------------------
// Speakers and their wall brackets
// ---------------------------------------------------------------------------

// Cabinet, grille, drivers and either a hanging bar (near an alcove opening)
// or a wall shelf bracket, plus a cord. Fills SPEAKER_ANCHORS with the front
// face of each cabinet for the audio rig.
function buildSpeakers(woodBucket, trimBucket) {
    SPEAKER_ANCHORS.length = 0;
    const halfW = ROOM.w / 2;
    for (const speaker of speakerRig()) {
        const side = Math.sign(speaker.x) || 1,
            isMain = speaker.role !== "fill",
            cabW = isMain ? .3 : .24,
            cabH = isMain ? .4 : .3,
            cabD = isMain ? .26 : .2,
            aimYaw = Math.atan2(speaker.aim.x - speaker.x, speaker.aim.z - speaker.z),
            frame = frameOf(speaker.x, speaker.z, aimYaw);
        partL(woodBucket, frame, new THREE.BoxGeometry(cabW, cabH, cabD), 0, speaker.y, 0);
        partL(trimBucket, frame, new THREE.BoxGeometry(cabW - .02, cabH - .02, .014), 0, speaker.y, cabD / 2 + .004);
        const coneR = isMain ? .098 : .076;
        partL(trimBucket, frame, new THREE.CylinderGeometry(coneR, coneR * .86, .018, 14), 0, speaker.y - cabH * .16, cabD / 2 + .012, Math.PI / 2, 0, 0);
        partL(trimBucket, frame, new THREE.CylinderGeometry(coneR * .34, coneR * .3, .016, 10), 0, speaker.y + cabH * .28, cabD / 2 + .012, Math.PI / 2, 0, 0);
        const wallFrame = frameOf(side * halfW, speaker.z, 0),
            inward = -side;
        if (nearAlcoveOpening(side, speaker.z, .25)) {
            const barLength = .9 + cabW;
            partL(trimBucket, wallFrame, new THREE.CylinderGeometry(.024, .024, barLength, 8), inward * .3, speaker.y + cabH / 2 + .16, 0, Math.PI / 2, 0, 0);
            for (const end of [-1, 1]) {
                partL(trimBucket, wallFrame, new THREE.BoxGeometry(.1, .05, .022), inward * .3, speaker.y + cabH / 2 + .16, end * (barLength / 2 - .03));
                partL(trimBucket, wallFrame, new THREE.BoxGeometry(.03, .2, .016), inward * .3, speaker.y + cabH / 2 + .07, end * (cabW / 2 - .03));
            }
        } else {
            const shelfY = speaker.y - cabH / 2 - .016;
            partL(woodBucket, wallFrame, new THREE.BoxGeometry(.4, .032, cabW + .16), inward * .2, shelfY, 0);
            partL(trimBucket, wallFrame, new THREE.BoxGeometry(.045, .34, cabW + .16), inward * .022, shelfY + .1, 0);
            for (const end of [-1, 1]) partL(trimBucket, wallFrame, new THREE.BoxGeometry(.3, .028, .03), inward * .17, shelfY - .12, end * (cabW / 2 + .03), 0, 0, side * .72);
        }
        const cordTop = speaker.y - cabH / 2 - .05,
            cordLength = Math.max(.2, cordTop - 1.45);
        partL(paper.cord, wallFrame, new THREE.CylinderGeometry(.006, .006, cordLength, 5), inward * .035, cordTop - cordLength / 2, cabW / 2 + .02);
        SPEAKER_ANCHORS.push({
            id: speaker.id,
            role: speaker.role,
            x: speaker.x + Math.sin(aimYaw) * (cabD / 2),
            y: speaker.y,
            z: speaker.z + Math.cos(aimYaw) * (cabD / 2),
            yaw: aimYaw
        });
    }
}

// ---------------------------------------------------------------------------
// The counter, the clerk, the listening chair and the back-wall unit
// ---------------------------------------------------------------------------

// The till on the customer-side slab: cash register with a sloped keypad, a
// customer display (emissive, lights nothing), card reader, service bell,
// receipt spike, a stack of kraft bags and a pen cup. `surface` is the y of the
// slab top; everything is in the counter's frame, customer side at -z.
function buildTill(frame, trimBucket, surface) {
    const s = surface;
    partL(paper.board, frame, new THREE.BoxGeometry(.38, .15, .3), .16, s + .075, -.9);
    partL(trimBucket, frame, new THREE.BoxGeometry(.32, .03, .01), .16, s + .06, -1.055);
    partL(trimBucket, frame, new THREE.BoxGeometry(.3, .035, .15), .16, s + .175, -.85, .45, 0, 0);
    partL(trimBucket, frame, new THREE.CylinderGeometry(.012, .012, .1, 6), .16, s + .2, -.8);
    partL(trimBucket, frame, new THREE.BoxGeometry(.2, .11, .03), .16, s + .29, -.8);
    partL(glow.warm, frame, new THREE.BoxGeometry(.17, .075, .006), .16, s + .29, -.8 - .018);
    partL(trimBucket, frame, new THREE.BoxGeometry(.07, .025, .11), -.12, s + .0125, -.98, -.2, 0, 0);
    partL(glow.warm, frame, new THREE.BoxGeometry(.04, .004, .02), -.12, s + .03, -1.01, -.2, 0, 0);
    partL(trimBucket, frame, new THREE.CylinderGeometry(.062, .066, .012, 12), -.3, s + .006, -.95);
    partL(trimBucket, frame, new THREE.SphereGeometry(.05, 12, 5, 0, Math.PI * 2, 0, Math.PI / 2), -.3, s + .012, -.95);
    partL(trimBucket, frame, new THREE.CylinderGeometry(.036, .036, .012, 8), .55, s + .006, -.95);
    partL(trimBucket, frame, new THREE.CylinderGeometry(.003, .006, .15, 6), .55, s + .087, -.95);
    for (let i = 0; i < 3; i++) partL(paper.board, frame, new THREE.BoxGeometry(.06, .003, .045), .55, s + .02 + i * .012, -.95, 0, i * .7, 0);
    for (let i = 0; i < 4; i++) partL(paper.kraft, frame, new THREE.BoxGeometry(.2, .034, .28), -.62, s + .017 + i * .034, -.88, 0, (i - 1.5) * .09, 0);
    partL(trimBucket, frame, new THREE.CylinderGeometry(.032, .028, .09, 8), .52, s + .045, -.74);
    for (const lean of [-.25, .2]) partL(trimBucket, frame, new THREE.CylinderGeometry(.004, .004, .12, 4), .52 + lean * .1, s + .13, -.74, lean, 0, -lean);
}

function buildCounter(parent, storeGroup, woodMaterial, trimMaterial) {
    const group = new THREE.Group,
        woodBucket = [],
        trimBucket = [],
        counterX = COUNTER_X,
        counterZ = COUNTER_Z,
        counterYaw = COUNTER_YAW;
    {
        const size = COUNTER_SIZE,
            topY = 1.06,
            span = size,
            frame = frameOf(counterX, counterZ, counterYaw),
            frontZ = -size / 2 - .006,
            gap = 1.1;
        for (const [faceX, faceZ, faceYaw] of [
                [0, -size / 2, 0],
                [0, size / 2, Math.PI],
                [-size / 2, 0, Math.PI / 2],
                [size / 2, 0, -Math.PI / 2]
            ]) {
            const panels = faceZ === -size / 2 || faceZ === size / 2 ? [
                [-1.7 / 2, (size - gap) / 2],
                [(size / 2 + gap / 2) / 2, (size - gap) / 2]
            ] : [
                [0, size]
            ];
            for (const [panelOffset, panelWidth] of panels) {
                const offX = panelOffset * Math.cos(faceYaw),
                    offZ = -panelOffset * Math.sin(faceYaw);
                partL(woodBucket, frame, new THREE.BoxGeometry(panelWidth, topY - .1, .09), faceX + offX, .1 + (topY - .1) / 2, faceZ + offZ, 0, faceYaw, 0);
                for (let i = 0; i < 5; i++) {
                    const railY = .15 + i * .175;
                    if (railY > topY - .14) break;
                    partL(trimBucket, frame, new THREE.BoxGeometry(panelWidth - .04, .16, .016), (faceX + offX) * 1.02, railY, (faceZ + offZ) * 1.02, 0, faceYaw, 0);
                }
            }
        }
        partL(woodBucket, frame, new THREE.BoxGeometry(size - .16, .1, size - .16), 0, .05, 0);
        // Closes the customer-side opening below the slab so the clerk's legs are not seen through it.
        partL(woodBucket, frame, new THREE.BoxGeometry(gap + .1, topY - .06, .05), 0, (topY - .06) / 2, -size / 2 + .2);
        const slabDepth = .62;
        for (const [slabX, slabZ, slabW, slabD] of [
                [0, -1.8399999999999999 / 2, size + .16, slabDepth],
                [0, (size + .16 - slabDepth) / 2, size + .16, slabDepth],
                [-1.8399999999999999 / 2, 0, slabDepth, size + .16 - slabDepth * 2],
                [(size + .16 - slabDepth) / 2, 0, slabDepth, size + .16 - slabDepth * 2]
            ]) partL(trimBucket, frame, new THREE.BoxGeometry(slabW, .075, slabD), slabX, topY + .037, slabZ);
        const signY = topY + 1.06;
        for (const [cornerX, cornerZ] of [
                [-1, -1],
                [1, -1],
                [-1, 1],
                [1, 1]
            ]) partL(trimBucket, frame, new THREE.BoxGeometry(.05, signY - topY - .04, .05), cornerX * (size / 2 - .1), topY + (signY - topY) / 2, cornerZ * (size / 2 - .1));
        for (const [boardX, boardZ, boardYaw, phrase] of [
                [0, -size / 2 - .02, 0, "NSPIRE RECORDS"],
                [0, size / 2 + .02, Math.PI, "NSPIRE RECORDS"],
                [-size / 2 - .02, 0, Math.PI / 2, "INFORMATION"],
                [size / 2 + .02, 0, -Math.PI / 2, "INFORMATION"]
            ]) {
            partL(paper.board, frame, new THREE.BoxGeometry(size - .1, .3, .03), boardX, signY, boardZ, 0, boardYaw, 0);
            const tile = SIGN_TILE_INDEX[phrase] ?? 0;
            partL(paper.label, frame, atlasQuad(labelAtlas(), tile, Math.min(size - .26, (size - .26) * labelFit(tile)), .245), boardX * 1.03, signY, boardZ * 1.03, 0, boardYaw + Math.PI, 0);
        }
        for (const [stripX, stripZ, stripYaw] of [
                [0, -size / 2, 0],
                [0, size / 2, Math.PI],
                [-size / 2, 0, Math.PI / 2],
                [size / 2, 0, -Math.PI / 2]
            ]) partL(glow.warm, frame, new THREE.BoxGeometry(size - .3, .024, .024), stripX * .98, signY - .19, stripZ * .98, 0, stripYaw, 0);
        {
            for (let i = 0; i < 5; i++) {
                const offset = -.55 + i * .13 + rand() * .05,
                    quad = atlasQuad(coverAtlas(), genreCell("soul", i * 3), LP_SIZE, LP_SIZE);
                quad.setAttribute("color", new THREE.BufferAttribute(new Float32Array(quad.attributes.position.count * 3).fill(.94), 3));
                partAt(paper.cover, quad, new THREE.Vector3(counterX - .02, topY + .082 + i * .006, counterZ + offset), new THREE.Quaternion().setFromEuler(new THREE.Euler(-Math.PI / 2, 0, counterYaw + (rand() - .5) * .3, "YXZ")));
            }
            for (let i = 0; i < 7; i++) partL(trimBucket, frame, new THREE.BoxGeometry(LP_SIZE, .009, LP_SIZE), .1, topY + .085 + i * .0095, .72, 0, (rand() - .5) * .12, 0);
            buildTill(frame, trimBucket, topY + .0745);
            part(paintParts, new THREE.CylinderGeometry(.038, .033, .1, 10), counterX + .1, topY + .09, counterZ + .3);
            partL(trimBucket, frame, new THREE.CylinderGeometry(.16, .16, .045, 12), .62, .66, -.1);
            for (let i = 0; i < 3; i++) {
                const legAngle = i * (Math.PI * 2 / 3);
                partL(trimBucket, frame, new THREE.CylinderGeometry(.016, .016, .64, 6), .62 + Math.cos(legAngle) * .11, .33, -.1 + Math.sin(legAngle) * .11, 0, 0, .06);
            }
            partL(trimBucket, frame, new THREE.BoxGeometry(.4, .9, span - .3), .92, .45, 0);
            partL(trimBucket, frame, new THREE.BoxGeometry(.46, .05, span - .24), .92, .925, 0);
            for (let i = 0; i < 26; i++) partL(trimBucket, frame, new THREE.BoxGeometry(LP_SIZE, LP_SIZE, .012), .92, 1.1, -1.5999999999999999 / 2 + i * ((span - .7) / 26), 0, 0, (rand() - .5) * .1);
        }
        tapedPlacard(frame, SIGN_TILE_INDEX["PAY HERE"] ?? 0, -.74, .8, frontZ, Math.PI, .3, .04);
        {
            const checkOutTile = SIGN_TILE_INDEX["CHECK OUT"] ?? 0,
                nameTile = SIGN_TILE_INDEX["NSPIRE RECORDS"] ?? 0;
            for (const side of [-1, 1]) {
                partL(paper.board, frame, new THREE.BoxGeometry(size - .34, .22, .026), 0, signY - .4, side * (size / 2 + .05));
                partL(paper.label, frame, atlasQuad(labelAtlas(), checkOutTile, (size - .5) * labelFit(checkOutTile), .175), 0, signY - .4, side * (size / 2 + .068), 0, side < 0 ? Math.PI : 0, 0);
            }
            for (const side of [-1, 1]) partL(paper.label, frame, atlasQuad(labelAtlas(), nameTile, (size - .3) * labelFit(nameTile) * .62, .105), 0, topY - .06, side * (size / 2 + .055), 0, side < 0 ? Math.PI : 0, 0);
            for (const side of [-1, 1]) {
                const panelCentre = side * (size / 2 + gap / 2) / 2,
                    panelHalf = (size - gap) / 4;
                flyerCollage(frame, panelCentre - panelHalf + .06, panelCentre + panelHalf - .06, .26, .86, -size / 2 - .055, -1, .55);
                shopSign(frame, panelCentre, .5, -size / 2 - .075, Math.PI, .2, side > 0 ? 4 : 8, .1);
            }
            shopSign(frame, -size / 2 - .075, .8, .55, -Math.PI / 2, .19, 2, .14);
            shopSign(frame, size / 2 + .075, .8, -.55, Math.PI / 2, .19, 13, .14);
        } {
            const clerk = new THREE.Mesh(figureGeometry(), figureMaterial(true));
            clerk.position.set(counterX + .34, 0, counterZ + .3);
            clerk.rotation.y = Math.PI;
            clerk.castShadow = true;
            clerk.name = "clerk";
            storeGroup.add(clerk);
        }
        partL(glow.warm, frame, new THREE.BoxGeometry(.03, .03, span - .5), .155, topY + .6, 0);
        partL(trimBucket, frame, new THREE.BoxGeometry(.05, .045, span - .44), .175, topY + .635, 0);
        partL(glow.warm, frame, new THREE.BoxGeometry(.022, .022, span - .6), frontZ + .03, topY - .075, 0);
        shopSign(frame, frontZ, .52, -.45, -Math.PI / 2, .24, null, .06);
        for (let i = 0; i < 3; i++) tapedFlyer(frame, frontZ, .6 + rand() * .24, .55 + (rand() - .5) * .5, -Math.PI / 2, .06 + rand() * .03);
        // The kiosk is a free-standing square, COUNTER_SIZE on a side, so its
        // collider is the whole footprint. This was 0.6 x 1.7 - a one-sided
        // counter's size - which left 85 of 121 sample points across the real
        // footprint walkable: shoppers (and the player) crossed the side panels.
        addCollider(counterX, counterZ, COUNTER_SIZE, COUNTER_SIZE);
    }
    part(trimBucket, new THREE.CylinderGeometry(.16, .16, .62, 8), counterX - .06, .31, 5.4);
    addCollider(counterX - .06, 5.4, .32, .32, .04); {
        const chairX = -(ROOM.w / 2 - .58),
            chairZ = -7.5,
            chairYaw = -Math.PI / 2 + .28,
            chairFrame = frameOf(chairX, chairZ, chairYaw);
        partL(woodBucket, chairFrame, new THREE.BoxGeometry(.72, .36, .7), 0, .3, 0);
        partL(woodBucket, chairFrame, new THREE.BoxGeometry(.16, .58, .7), -.28, .66, 0);
        for (const side of [-1, 1]) partL(woodBucket, chairFrame, new THREE.BoxGeometry(.6, .2, .14), .03, .56, side * .31);
        for (const sideX of [-1, 1])
            for (const sideZ of [-1, 1]) partL(woodBucket, chairFrame, new THREE.BoxGeometry(.07, .12, .07), sideX * .3, .06, sideZ * .28);
        part(trimBucket, new THREE.BoxGeometry(.42, .03, .42), chairX + .62, .54, chairZ + .3, 0, .2, 0);
        for (const sideX of [-1, 1])
            for (const sideZ of [-1, 1]) part(trimBucket, new THREE.BoxGeometry(.035, .52, .035), chairX + .62 + sideX * .17, .27, chairZ + .3 + sideZ * .17);
        for (let i = 0; i < 5; i++) part(trimBucket, new THREE.BoxGeometry(LP_SIZE, .008, LP_SIZE), chairX + .62, .56 + i * .01, chairZ + .3, 0, .2 + (rand() - .5) * .3, 0);
        part(trimBucket, new THREE.CylinderGeometry(.038, .034, .09, 10), chairX + .74, .6, chairZ + .44);
        part(trimBucket, new THREE.CylinderGeometry(.018, .024, 1.4, 8), chairX - .62, .7, chairZ + .55);
        part(trimBucket, new THREE.ConeGeometry(.15, .22, 10, 1, true), chairX - .62, 1.46, chairZ + .55);
        addCollider(chairX, chairZ, .72, .72, .02);
    } {
        const unitX = ROOM.w / 2 - .62,
            unitZ = -.3;
        part(woodBucket, new THREE.BoxGeometry(.46, .94, .4), unitX, .47, unitZ);
        part(trimBucket, new THREE.BoxGeometry(.5, .04, .44), unitX, .96, unitZ);
        part(trimBucket, new THREE.BoxGeometry(.34, .06, .3), unitX - .02, 1, unitZ, 0, .1, 0);
        part(trimBucket, new THREE.CylinderGeometry(.075, .075, .012, 14), unitX - .02, 1.04, unitZ);
        part(trimBucket, new THREE.BoxGeometry(.6, .4, .44), unitX - .05, .2, unitZ + 1.1, 0, .14, 0);
        addCollider(unitX, unitZ, .5, .5, .14);
    }
    buildSpeakers(woodBucket, trimBucket);
    flushBucket(group, woodBucket, woodMaterial);
    const trimMesh = flushBucket(group, trimBucket, trimMaterial);
    if (trimMesh) trimMesh.castShadow = false;
    part(glow.bulb, new THREE.SphereGeometry(.026, 10, 8), counterX + .17, 1.46, counterZ - .55);
    parent.add(group);
    return group;
}

// True when (side, z) is within `pad` of an alcove opening on that wall.
function nearAlcoveOpening(side, z, pad = .6) {
    return ALCOVES.some(zone => zone.alcove.side === side && Math.abs(z - zone.alcove.z) < zone.alcove.openingW / 2 + pad);
}

// ---------------------------------------------------------------------------
// Floor clutter
// ---------------------------------------------------------------------------

// Stacked kraft crates down the middle of the floor, a leaning rack by the
// back-right corner, and posters taped low on both side walls.
function buildFloorCrates(unused) {
    const kraft = paper.kraft,
        worldFrame = frameOf(0, 0, 0),
        spots = [
            [-1.45, 7.3],
            [1.45, 4.05],
            [0, -1.1],
            [-1.45, -4.3],
            [1.45, -6.3]
        ];
    genreTree();
    spots.forEach(([spotX, spotZ], index) => {
        const style = zoneStyle(zoneAtZ(spotZ)),
            buckets = bucketsFor(style),
            genreId = BAYS.reduce((best, bay) => Math.abs(bay.z - spotZ) < Math.abs(best.z - spotZ) ? bay : best, BAYS[0]).g,
            yaw = (rand() - .5) * .9,
            frame = frameOf(spotX + (rand() - .5) * .25, spotZ + (rand() - .5) * .4, yaw);
        if (index % 3 === 0) {
            const stackCount = 2 + Math.floor(rand() * 2);
            for (let i = 0; i < stackCount; i++) partL(kraft, frame, new THREE.BoxGeometry(.4, .29, .34), (rand() - .5) * .05, .145 + i * .3, (rand() - .5) * .05, 0, (rand() - .5) * .2, 0);
            for (const spine of spineRun(.3)) addSpine(buckets, frame, spine.x, .3 * stackCount + .02 + spine.dy, spine.dz * .3, spine.lean, spine.t, .62, genreId);
            shopSign(frame, 0, .3 * stackCount - .16, .175, 0, .22, index);
        } else if (index % 3 === 1) {
            partL(kraft, frame, new THREE.BoxGeometry(.44, .26, .38), 0, .13, 0);
            for (const side of [-1, 1]) partL(kraft, frame, new THREE.BoxGeometry(.44, .2, .008), 0, .34, side * .19, side * .35, 0, 0);
            for (const spine of spineRun(.32)) addSpine(buckets, frame, spine.x, .3 + spine.dy, spine.dz * .4, spine.lean * 1.4, spine.t, .6, genreId);
        }
    }); {
        const rackX = ROOM.w / 2 - 1.35,
            rackZ = -ROOM.d / 2 + .55,
            rackFrame = frameOf(rackX, rackZ, .5);
        for (const side of [-1, 1]) {
            partL(kraft, rackFrame, new THREE.BoxGeometry(.05, 1.55, .05), side * .22, .78, 0, .1, 0, 0);
            partL(kraft, rackFrame, new THREE.BoxGeometry(.05, 1.42, .05), side * .22, .71, .34, -.14, 0, 0);
        }
        for (let i = 0; i < 4; i++) partL(kraft, rackFrame, new THREE.BoxGeometry(.48, .028, .16), 0, .28 + i * .33, -.03 - i * .032);
    }
    for (const side of [-1, 1]) {
        const wallX = side * (ROOM.w / 2 - .03);
        for (let i = 0; i < 4; i++) {
            const z = -6.4 + i * 3.7 + (rand() - .5) * .5;
            if (!(side > 0 && z > 5.6 && z < 8) && !nearAlcoveOpening(side, z, .9)) {
                // Loop bound re-evaluated each pass, so it draws a fresh random
                // count every iteration.
                for (let j = 0; j < 3 + Math.floor(rand() * 3); j++) {
                    const height = .16 + rand() * .16,
                        quad = atlasQuad(posterAtlas(), Math.floor(rand() * POSTER_COUNT), height * .7, height);
                    part(paper.poster, quad, wallX - side * (.004 + j % 3 * .004), 1.62 + (rand() - .5) * .55, z + (rand() - .5) * .7, 0, side > 0 ? -Math.PI / 2 : Math.PI / 2, (rand() - .5) * .5);
                }
                shopSign(worldFrame, wallX - side * .012, 1.42 + (rand() - .5) * .3, z + (rand() - .5) * .5, side > 0 ? -Math.PI / 2 : Math.PI / 2, .24);
            }
        }
    }
}

// Framed prints down both side walls and across the back wall: frame box, card
// mount, poster quad, each layered forward off the wall.
function buildFramedPictures() {
    const placements = [];
    for (const side of [-1, 1]) {
        const wallX = side * (ROOM.w / 2 - .02),
            ry = side > 0 ? -Math.PI / 2 : Math.PI / 2;
        for (let i = 0; i < 5; i++) {
            const z = -7.9 + i * .62,
                height = .36 + i % 2 * .14,
                width = height * .76;
            placements.push([wallX, 1.72 + i % 2 * .1, z, ry, width, height, side]);
        }
    }
    for (let i = 0; i < 3; i++) placements.push([-.9 + i * .9, 1.8, -ROOM.d / 2 + .04, 0, .4, .5, 0]);
    for (const [x, y, z, ry, width, height, side] of placements) {
        const step = side === 0 ? .02 : -side * .02,
            dx = side === 0 ? 0 : step,
            dz = side === 0 ? step : 0;
        part(paper.frame, new THREE.BoxGeometry(width + .06, height + .06, .028), x + dx, y, z + dz, 0, ry, 0);
        part(paper.card, new THREE.PlaneGeometry(width + .01, height + .01), x + dx * 1.9, y, z + dz * 1.9, 0, ry, 0);
        part(paper.poster, atlasQuad(posterAtlas(), Math.floor(rand() * POSTER_COUNT), width * .8, height * .8), x + dx * 2.2, y, z + dz * 2.2, 0, ry, 0);
    }
}

// ---------------------------------------------------------------------------
// Alcoves
// ---------------------------------------------------------------------------

// The entrance: jambs and lintel with the shop name over the arch, a glazed
// door, a mat, flyer collages on the two return walls and two battens.
function buildEntranceAlcove(group, zone, alcove) {
    const buckets = bucketsFor(zone),
        centreX = alcove.side * (ROOM_HALF_W + alcove.w / 2),
        wallX = alcove.side * ROOM_HALF_W,
        worldFrame = frameOf(0, 0, 0),
        outerX = centreX + alcove.side * (alcove.w / 2),
        openingH = Math.min(2.72, (alcove.ceil ?? 2.8) - .3);
    for (const side of [-1, 1]) {
        const jambZ = alcove.z + side * (alcove.openingW / 2 + .06);
        partL(buckets.wood, worldFrame, new THREE.BoxGeometry(.35, openingH, .1), wallX + alcove.side * .175, openingH / 2, jambZ);
    }
    partL(buckets.wood, worldFrame, new THREE.BoxGeometry(.35, .34, alcove.openingW + .34), wallX + alcove.side * .175, openingH + .17, alcove.z);
    partL(paper.label, worldFrame, atlasQuad(labelAtlas(), SIGN_TILE_INDEX["NSPIRE RECORDS"] ?? 0, alcove.openingW * .82, .26), wallX - alcove.side * .012, openingH + .17, alcove.z, 0, alcove.side > 0 ? -Math.PI / 2 : Math.PI / 2, 0);
    const doorW = 1.15,
        doorH = 2.1;
    for (const side of [-1, 1]) partL(buckets.wood, worldFrame, new THREE.BoxGeometry(.09, doorH + .1, .08), outerX - alcove.side * .05, (doorH + .1) / 2, alcove.z + side * (doorW / 2 + .04));
    partL(buckets.wood, worldFrame, new THREE.BoxGeometry(.09, .08, doorW + .18), outerX - alcove.side * .05, doorH + .05, alcove.z);
    const glassH = (doorH - .14) * .66;
    part(glow.daylight, new THREE.BoxGeometry(.02, glassH, doorW - .12), outerX - alcove.side * .075, doorH - .07 - glassH / 2, alcove.z);
    partL(buckets.wood, worldFrame, new THREE.BoxGeometry(.05, doorH - .14 - glassH, doorW - .1), outerX - alcove.side * .075, (doorH - .14 - glassH) / 2 + .02, alcove.z);
    partL(buckets.trim, worldFrame, new THREE.CylinderGeometry(.018, .018, doorW - .34, 6), outerX - alcove.side * .1, 1.02, alcove.z, Math.PI / 2, 0, 0);
    shopSign(worldFrame, outerX - alcove.side * .095, 1.62, alcove.z, alcove.side > 0 ? -Math.PI / 2 : Math.PI / 2, .22);
    partL(buckets.wood, worldFrame, new THREE.BoxGeometry(1.05, .012, 1.55), outerX - alcove.side * .85, .007, alcove.z);
    for (const side of [-1, 1]) {
        const returnZ = alcove.z + side * (alcove.d / 2 - .02),
            returnFrame = frameOf(0, returnZ, side > 0 ? Math.PI : 0);
        flyerCollage(returnFrame, centreX - alcove.w / 2 + .15, centreX + alcove.w / 2 - .15, .3, 2.3, 0, 1, .85);
        for (let i = 0; i < 5; i++) shopSign(returnFrame, centreX - alcove.w / 2 + .4 + rand() * (alcove.w - .8), 1.5 + rand() * .7, .02, 0, .24);
    }
    for (const offset of [-.8, .8]) part(glow.batten, new THREE.BoxGeometry(alcove.w - .5, .03, .05), centreX, alcove.ceil - .06, alcove.z + offset);
    addCollider(outerX - alcove.side * .02, alcove.z, .1, alcove.d, .02);
}

// Any other alcove: jambs and lintel, two bays with a split subgenre window,
// the room's own dressing, and a lettered board hung across the opening.
function buildAlcove(group, zoneDef, materials) {
    const style = zoneStyle(zoneDef),
        alcove = style.alcove;
    if (zoneDef.entrance) return buildEntranceAlcove(group, style, alcove);
    const buckets = bucketsFor(style),
        centreX = alcove.side * (ROOM_HALF_W + alcove.w / 2),
        wallX = alcove.side * ROOM_HALF_W,
        genreId = style.genres[0],
        jambD = .35,
        openingH = Math.min(2.72, (alcove.ceil ?? 2.8) - .3);
    for (const side of [-1, 1]) {
        const jambZ = alcove.z + side * (alcove.openingW / 2 + .06);
        partL(buckets.wood, frameOf(0, 0, 0), new THREE.BoxGeometry(jambD, openingH, .1), wallX + alcove.side * jambD / 2, openingH / 2, jambZ + side * .05);
    }
    partL(buckets.wood, frameOf(0, 0, 0), new THREE.BoxGeometry(jambD, .3, alcove.openingW + .34), wallX + alcove.side * jambD / 2, openingH + .15, alcove.z);
    const backSeg = {
            g: genreId,
            subs: [0, 4],
            z: alcove.z,
            run: 0,
            len: 1
        },
        sideSeg = {
            g: genreId,
            subs: [4, 7],
            z: alcove.z,
            run: 0,
            len: 1
        };
    if (style.id === "listening-room") {
        buildBay(group, style, centreX, alcove.z - alcove.d / 2 + .42, alcove.w - .5, 0, materials, 1, {
            depth: .46,
            seg: backSeg
        });
        buildBay(group, style, centreX - alcove.w / 2 + .3, alcove.z + .35, alcove.d - 1.2, Math.PI / 2, materials, 1, {
            depth: .44,
            seg: sideSeg
        });
        const tableX = centreX + alcove.w / 2 - .45,
            tableZ = alcove.z + alcove.d / 2 - .55;
        partL(buckets.wood, frameOf(0, 0, 0), new THREE.BoxGeometry(.42, .04, .42), tableX, .56, tableZ);
        for (const sideX of [-1, 1])
            for (const sideZ of [-1, 1]) partL(buckets.wood, frameOf(0, 0, 0), new THREE.BoxGeometry(.035, .56, .035), tableX + sideX * .17, .28, tableZ + sideZ * .17);
        partL(buckets.trim, frameOf(0, 0, 0), new THREE.CylinderGeometry(.015, .015, .42, 8), tableX, .79, tableZ);
        part(glow.warm, new THREE.CylinderGeometry(.16, .21, .24, 12, 1, true), tableX, 1.08, tableZ);
        const poster = atlasQuad(posterAtlas(), 7, .42, .56);
        part(paper.poster, poster, centreX, 1.72, alcove.z - alcove.d / 2 + .06);
        part(paper.frame, new THREE.BoxGeometry(.5, .64, .03), centreX, 1.72, alcove.z - alcove.d / 2 + .02);
    } else {
        const side = alcove.side;
        buildBay(group, style, centreX, alcove.z + alcove.d / 2 - .42, alcove.w - .5, 0, materials, -1, {
            depth: .46,
            posts: false,
            seg: backSeg
        });
        buildBay(group, style, centreX + side * (alcove.w / 2 - .3), alcove.z - .2, alcove.d - 1.3, Math.PI / 2, materials, -side, {
            depth: .44,
            posts: false,
            seg: sideSeg
        });
        const stackX = centreX - side * (alcove.w / 2 - .42),
            stackZ = alcove.z - alcove.d / 2 + .45;
        for (let i = 0; i < 3; i++) {
            const y = .28 + i * .54;
            partL(buckets.wood, frameOf(0, 0, 0), new THREE.BoxGeometry(.62, .52, .44), stackX, y, stackZ);
            partL(buckets.trim, frameOf(0, 0, 0), new THREE.CylinderGeometry(.16, .16, .04, 12), stackX, y, stackZ + .23, Math.PI / 2, 0, 0);
            part(paintParts, new THREE.BoxGeometry(.62, .045, .02), stackX, y + .22, stackZ + .225);
            part(paintParts, new THREE.BoxGeometry(.14, .03, .05), stackX + .22, y - .2, stackZ + .235);
        }
        partL(buckets.wood, frameOf(0, 0, 0), new THREE.BoxGeometry(.44, .24, .34), stackX, 1.46, stackZ + .04, 0, -.34, 0);
        addCollider(stackX, stackZ, .62, .44, .12);
        const crateX = centreX + side * (alcove.w / 2 - .44),
            crateZ = alcove.z + alcove.d / 2 - .62;
        for (let i = 0; i < 3; i++) {
            const y = .145 + i * .3,
                x = crateX + (rand() - .5) * .06,
                yawJitter = (rand() - .5) * .34;
            part(paintParts, new THREE.BoxGeometry(.42, .28, .38), x, y, crateZ, 0, yawJitter, 0);
            if (i === 2)
                for (let j = 0; j < 8; j++) {
                    const quad = atlasQuad(coverAtlas(), genreCell(genreId, j), .17, .17),
                        vertexCount = quad.attributes.position.count;
                    quad.setAttribute("color", new THREE.BufferAttribute(new Float32Array(vertexCount * 3).fill(.86 + rand() * .12), 3));
                    part(paper.cover, quad, x + (rand() - .5) * .02, y + .11, crateZ - .15 + j * .036, -.13, yawJitter, (rand() - .5) * .07);
                }
            shopSign(frameOf(0, 0, 0), x - .21 - .004, y, crateZ + (rand() - .5) * .1, -Math.PI / 2, .17, i === 2 ? 4 : 2, .16);
        }
        const wallFrame = frameOf(centreX + alcove.side * (alcove.w / 2 - .03), alcove.z, alcove.side > 0 ? -Math.PI / 2 : Math.PI / 2);
        flyerCollage(wallFrame, -alcove.d / 2 + .22, alcove.d / 2 - .22, .95, 2.05, 0, 1, .85);
        for (let i = 0; i < 4; i++) shopSign(wallFrame, -alcove.d / 2 + .35 + rand() * (alcove.d - .9), 1.05 + rand() * .85, .012, 0, .19 + rand() * .07, [4, 10, 8, 13][i], .2);
        const bulbX = centreX,
            bulbY = 2.1,
            bulbZ = alcove.z;
        part(paper.cord, new THREE.CylinderGeometry(.004, .004, 2.3 - bulbY, 5), bulbX, (bulbY + 2.3) / 2 + .06, bulbZ);
        part(glow.bulb, new THREE.SphereGeometry(.03, 10, 8), bulbX, bulbY, bulbZ);
    }
    const signRy = alcove.side > 0 ? -Math.PI / 2 : Math.PI / 2,
        worldFrame = frameOf(0, 0, 0),
        signY = 2.34,
        signW = alcove.openingW - .3,
        signX = wallX + alcove.side * .05;
    part(paper.board, new THREE.BoxGeometry(.03, .26, signW), signX, signY, alcove.z);
    for (const face of [1, -1]) {
        const ry = signRy + (face > 0 ? 0 : Math.PI),
            tile = LABEL_TILE_INDEX[genreId] ?? 0;
        partL(paper.label, worldFrame, atlasQuad(labelAtlas(), tile, Math.min(signW - .12, .62 * labelFit(tile)), .18), signX - alcove.side * face * .017, signY, alcove.z, 0, ry, 0);
    }
    for (const side of [-1, 1]) part(paper.cord, new THREE.CylinderGeometry(.004, .004, 3.14 - .06 - signY, 5), signX, (signY + .13 + 3.14) / 2, alcove.z + side * (signW / 2 - .1));
    shopSign(worldFrame, wallX - alcove.side * .014, 1.34, alcove.z + alcove.openingW / 2 + .3, signRy, .2, 10, .12);
}

// ---------------------------------------------------------------------------
// buildStore — the master assembly
// ---------------------------------------------------------------------------

function buildStore(scene) {
    const storeGroup = new THREE.Group,
        lipBucket = [];
    storeGroup.userData.lipBucket = lipBucket;
    const materials = {};
    labelAtlas();
    coverAtlas();
    const alcoveSpans = ALCOVES.map(zone => ({
            side: zone.alcove.side,
            z0: zone.alcove.z - zone.alcove.openingW / 2 - .35,
            z1: zone.alcove.z + zone.alcove.openingW / 2 + .35
        })),
        entrance = ALCOVES.find(zone => zone.entrance),
        entranceRect = entrance ? {
            x0: entrance.alcove.side > 0 ? ROOM_HALF_W - 3.1 : -ROOM_HALF_W,
            x1: entrance.alcove.side > 0 ? ROOM_HALF_W : -ROOM_HALF_W + 3.1,
            z0: entrance.alcove.z - 2.1,
            z1: entrance.alcove.z + 2.1
        } : null,
        counterRect = COUNTER_RECT,
        overlapsRect = (rect, x, z0, z1) => rect && x > rect.x0 - .3 && x < rect.x1 + .3 && z1 > rect.z0 - .3 && z0 < rect.z1 + .3,
        takenSlots = new Set,
        blocked = (x, z0, z1) => {
            if (overlapsRect(entranceRect, x, z0, z1) || overlapsRect(counterRect, x, z0, z1)) return true;
            if (Math.abs(x) < ROOM_HALF_W - 1.1) return false;
            const side = Math.sign(x);
            return alcoveSpans.some(span => span.side === side && z1 > span.z0 && z0 < span.z1);
        };
    for (const bay of BAYS) {
        const style = zoneStyle(zoneAtZ(bay.z)),
            plannedX = RUN_X[bay.run];
        let z = bay.z,
            x = plannedX;
        if (blocked(x, z - bay.len / 2, z + bay.len / 2)) {
            let placed = false;
            for (const band of Object.keys(BANDS)) {
                for (const run of [0, 1, 2, 3]) {
                    const runX = RUN_X[run],
                        bandZ = BANDS[band].z;
                    if (!blocked(runX, bandZ - bay.len / 2, bandZ + bay.len / 2) && !BAYS.some(other => other !== bay && other.run === run && other.band === band) && !takenSlots.has(`${run}/${band}`)) {
                        takenSlots.add(`${run}/${band}`);
                        x = runX;
                        z = bandZ;
                        placed = true;
                        break;
                    }
                }
                if (placed) break;
            }
            if (!placed) {
                console.warn(`[store] no free slot for bay ${bay.g} — its records are off the floor`);
                continue;
            }
        }
        const yaw = Math.PI / 2,
            kind = bay.g === "jazz" ? "shelf-deep" : bay.g === "electronic" ? "crate" : bay.g === "hiphop" ? "crate-12" : undefined,
            timber = bay.g === "jazz" ? "dark-stained" : bay.g === "electronic" ? "painted-black" : undefined,
            againstWall = Math.abs(x) > 1.5,
            face = -Math.sign(x) || 1;
        buildBay(storeGroup, style, x, z, bay.len, yaw, materials, face, {
            posts: !againstWall && plannedX < 0,
            stacked: againstWall,
            seg: bay,
            kind: kind,
            timber: timber,
            runX: x,
            twoSided: !againstWall
        });
        if (WIDE_ROOM && againstWall) {
            const side = Math.sign(x),
                shelfLength = Math.min(bay.len + .2, 3.2);
            if (!nearAlcoveOpening(side, z, shelfLength / 2)) buildWallShelf(storeGroup, style, side, z, shelfLength, 1.18, bay.g === "jazz" ? 3 : 4, materials, bay.g === "hiphop" || bay.g === "disco" || bay.g === "rnb", bay);
        }
    }
    for (const table of FLICK_TABLES) {
        const band = BANDS[table.band],
            style = zoneStyle(zoneAtZ(band.z));
        buildFlickTable(style, RUN_X[table.run], band.z, table.sign);
    }
    for (const zone of ALCOVES) buildAlcove(storeGroup, zone, materials);
    buildFloorCrates();
    buildFramedPictures();
    const woodMaterials = {},
        trimMaterials = {};
    for (const id of Object.keys(zoneBuckets)) {
        const buckets = zoneBuckets[id],
            zone = buckets.sec,
            tint = zone.fixture.tint ?? 10521709,
            timber = zone.fixture.timber;
        const timberMap = woodTexture(timber === "blackened" || timber === "painted-black" ? -46 : timber === "dark-stained" ? -26 : 0);
        woodMaterials[id] = new THREE.MeshStandardMaterial({
            map: timberMap,
            bumpMap: timberMap,
            bumpScale: WOOD_BUMP,
            color: tint,
            roughness: timber === "painted-black" ? .72 : .88,
            metalness: 0
        });
        if (zone.fixture.trim === "chrome") trimMaterials[id] = new THREE.MeshStandardMaterial({
            color: 14541802,
            roughness: .16,
            metalness: .55
        });
        else if (zone.fixture.trim === "brass") trimMaterials[id] = new THREE.MeshStandardMaterial({
            color: 13081157,
            roughness: .3,
            metalness: .5
        });
        else trimMaterials[id] = woodMaterials[id]; {
            const mesh = flushBucket(storeGroup, buckets.wood, woodMaterials[id]);
            if (mesh) mesh.name = "wood:" + id;
        }
        if (buckets.trim.length) {
            const mesh = flushBucket(storeGroup, buckets.trim, trimMaterials[id]);
            if (mesh) {
                mesh.name = "trim:" + id;
                mesh.castShadow = false;
            }
        }
    }
    buildSpineInstances(storeGroup);
    buildSleeveInstances(storeGroup);
    if (cassetteMatrices.length) {
        const mesh = new THREE.InstancedMesh(new THREE.BoxGeometry(.105, .068, .017), new THREE.MeshStandardMaterial({
            color: 16777215,
            roughness: .42,
            metalness: 0
        }), cassetteMatrices.length);
        mesh.name = "cassettes";
        mesh.castShadow = false;
        mesh.receiveShadow = true;
        const color = new THREE.Color;
        cassetteMatrices.forEach((matrix, index) => {
            mesh.setMatrixAt(index, matrix);
            color.setRGB(.72, .71, .69).offsetHSL(0, 0, (index * 13 % 20 - 10) / 120);
            if (index % 7 === 0) color.setRGB(.13, .12, .11);
            if (index % 11 === 0) color.setRGB(.62, .48, .18);
            mesh.setColorAt(index, color);
        });
        mesh.instanceMatrix.needsUpdate = true;
        mesh.instanceColor.needsUpdate = true;
        storeGroup.add(mesh);
    } {
        const dividers = [];
        for (const id of Object.keys(zoneBuckets)) dividers.push(...zoneBuckets[id].divs);
        if (dividers.length) {
            const mesh = new THREE.InstancedMesh(new THREE.BoxGeometry(.006, LP_SIZE + .05, LP_SIZE - .02), new THREE.MeshStandardMaterial({
                color: 11903874,
                roughness: .96
            }), dividers.length);
            dividers.forEach((matrix, index) => mesh.setMatrixAt(index, matrix));
            mesh.castShadow = false;
            mesh.receiveShadow = true;
            mesh.instanceMatrix.needsUpdate = true;
            storeGroup.add(mesh);
        }
    }
    const counterWoodMaterial = new THREE.MeshStandardMaterial({
            map: woodTexture(0),
            bumpMap: woodTexture(0),
            bumpScale: WOOD_BUMP,
            roughness: .88,
            metalness: 0,
            color: 11574139
        }),
        counterTrimMaterial = new THREE.MeshStandardMaterial({
            map: woodTexture(-18),
            bumpMap: woodTexture(-18),
            bumpScale: WOOD_BUMP,
            roughness: .84,
            metalness: 0,
            color: 10521709
        });
    buildCounter(scene, storeGroup, counterWoodMaterial, counterTrimMaterial);
    buildUpperPosters();
    buildPosterWall();
    buildRackTopClutter();
    buildSignage(storeGroup); {
        const atlas = coverAtlas(),
            heldMaterial = new THREE.MeshStandardMaterial({
                name: "held:cover",
                map: atlas.tex,
                roughness: .86
            }),
            placements = [
                ["rockpsych", .72, 6.68, .9],
                ["disco", .72, 2.9, .9],
                ["classical", -(ROOM.w / 2 - .58) + .62, -7.2, .6]
            ];
        for (const [genreId, x, z, y] of placements) {
            const heroes = GENRE_HERO_RECORDS[genreId] || [];
            if (!heroes.length) continue;
            const record = heroes[Math.floor(rand() * heroes.length)],
                cell = genreCell(genreId, heroes.indexOf(record)),
                geometry = new THREE.BoxGeometry(LP_SIZE, LP_SIZE, .009),
                column = cell % atlas.cols,
                row = Math.floor(cell / atlas.cols) % atlas.rows,
                uv = geometry.attributes.uv;
            for (let i = 0; i < uv.count; i++) uv.setXY(i, (column + uv.getX(i)) / atlas.cols, (atlas.rows - row - 1 + uv.getY(i)) / atlas.rows);
            const mesh = new THREE.Mesh(geometry, heldMaterial);
            mesh.position.set(x, y, z);
            mesh.rotation.set(Math.PI / 2 - .95 - rand() * .12, Math.PI / 2, (rand() - .5) * .12);
            mesh.castShadow = false;
            mesh.userData = {
                pickable: true,
                title: `${record.artist} — ${record.title}`,
                record: record,
                artist: record.artist,
                album: record.title,
                year: record.year,
                label: record.label,
                genre: genresById[record.genreId]?.label || record.genreId,
                subgenre: record.subgenreLabel,
                tracks: record.tracks,
                frontUrl: frontUrl(record),
                backUrl: backUrl(record),
                home: {
                    pos: mesh.position.clone(),
                    rot: mesh.rotation.clone(),
                    parent: storeGroup
                }
            };
            storeGroup.add(mesh);
            PICKABLES.push(mesh);
        }
    }
    const paperGroup = new THREE.Group;
    flushBucket(paperGroup, paper.poster, new THREE.MeshStandardMaterial({
        name: "paper:poster",
        map: posterAtlas().tex,
        roughness: .95,
        metalness: 0,
        side: THREE.DoubleSide,
        transparent: true,
        alphaTest: .5,
        polygonOffset: true,
        polygonOffsetFactor: -2,
        polygonOffsetUnits: -4
    }), false);
    flushBucket(paperGroup, paper.card, new THREE.MeshStandardMaterial({
        name: "paper:card",
        map: cardAtlas().tex,
        roughness: .95,
        metalness: 0,
        side: THREE.DoubleSide,
        transparent: true,
        alphaTest: .5,
        polygonOffset: true,
        polygonOffsetFactor: -2,
        polygonOffsetUnits: -4
    }), false);
    flushBucket(paperGroup, paper.cover, new THREE.MeshStandardMaterial({
        name: "paper:cover",
        map: coverAtlas().tex,
        roughness: .87,
        metalness: 0,
        side: THREE.FrontSide,
        vertexColors: true
    }), false);
    flushBucket(paperGroup, paper.label, new THREE.MeshStandardMaterial({
        name: "paper:label",
        map: labelAtlas().tex,
        roughness: .95,
        metalness: 0,
        side: THREE.FrontSide,
        polygonOffset: true,
        polygonOffsetFactor: -2,
        polygonOffsetUnits: -4
    }), false);
    const boardMaterial = new THREE.MeshStandardMaterial({
        name: "paper:board",
        map: woodTexture(12),
        color: 12627596,
        roughness: .94,
        metalness: 0
    });
    flushBucket(paperGroup, paper.board, boardMaterial, false);
    flushBucket(paperGroup, paper.kraft, boardMaterial, true);
    flushBucket(paperGroup, paper.frame, new THREE.MeshStandardMaterial({
        name: "paper:frame",
        map: woodTexture(-34),
        color: 6967864,
        roughness: .62,
        metalness: 0,
        polygonOffset: true,
        polygonOffsetFactor: -2,
        polygonOffsetUnits: -4
    }), false);
    flushBucket(paperGroup, paintParts, new THREE.MeshStandardMaterial({
        name: "paint:dub",
        map: woodTexture(4),
        color: 12869916,
        roughness: .66,
        metalness: .04
    }), true);
    flushBucket(paperGroup, paper.cord, new THREE.MeshStandardMaterial({
        name: "paper:cord",
        color: 1709330,
        roughness: 1
    }), false);
    flushBucket(paperGroup, lipBucket, counterTrimMaterial, true);
    // Emissive fittings: MeshBasicMaterial, so they glow but light nothing.
    const flushGlow = (bucket, r, g, b) => {
        if (!bucket.length) return;
        const material = new THREE.MeshBasicMaterial({
            fog: false
        });
        material.color.setRGB(r, g, b, THREE.LinearSRGBColorSpace);
        flushBucket(paperGroup, bucket, material, false);
    };
    flushGlow(glow.batten, .52, .5, .46);
    flushGlow(glow.warm, .6, .51, .4);
    flushGlow(glow.bulb, 2.3, 1.95, 1.15);
    flushGlow(glow.neonRed, 1.35, .19, .26);
    flushGlow(glow.neonCyan, .17, 1.05, 1.32);
    flushGlow(glow.daylight, 1.55, 1.72, 2.05);
    if (typeof window < "u" && window.__DEBUG_TRIS) {
        const rows = [];
        for (const root of [storeGroup, paperGroup]) root.traverse(node => {
            if (!node.geometry) return;
            const geometry = node.geometry,
                tris = (geometry.index ? geometry.index.count : geometry.attributes.position.count) / 3 * (node.isInstancedMesh ? node.count : 1);
            rows.push([node.name || (node.isInstancedMesh ? "INST" : "mesh"), Math.round(tris)]);
        });
        rows.sort((a, b) => b[1] - a[1]);
        console.log("TRIS", JSON.stringify(rows.slice(0, 25)));
    }
    scene.add(paperGroup);
    scene.add(storeGroup);
    return storeGroup;
}

// ---------------------------------------------------------------------------
// Neon, fascia and the three poster / clutter passes
// ---------------------------------------------------------------------------

// The lit shop name over the entrance arch, the back-wall "RECORDS" neon drawn
// as stroke segments, two wall neons, the hanging tube, and the mirror-tile
// panel over the groove floor.
function buildSignage(parent) {
    {
        const entrance = ALCOVES.find(zone => zone.entrance);
        if (entrance) {
            const alcove = entrance.alcove,
                x = -alcove.side * (ROOM.w / 2 - .06),
                ry = -alcove.side > 0 ? -Math.PI / 2 : Math.PI / 2,
                width = 1.92,
                height = .72;
            part(paper.board, new THREE.BoxGeometry(.05, height + .22, width + .26), x + Math.sign(x) * .03, 2.05, alcove.z);
            part(paper.label, atlasQuad(labelAtlas(), SIGN_TILE_INDEX["NSPIRE RECORDS"] ?? 0, width, height), x, 2.05, alcove.z, 0, ry, 0);
            for (const offset of [-width / 2 + .25, width / 2 - .25]) part(glow.warm, new THREE.BoxGeometry(.026, .026, .5), x - Math.sign(x) * .16, 2.05 - height / 2 - .12, alcove.z + offset);
        }
    } {
        const z = -ROOM.d / 2 + .09,
            centreY = 2.28,
            glyphHeight = .3,
            tubeRadius = .019,
            // Stroke list for "R"; reused for both R glyphs in RECORDS.
            strokeR = [
                [0, 0, 0, 1],
                [0, 1, .7, 1],
                [.7, 1, .7, .55],
                [.7, .55, 0, .55],
                [.25, .55, .75, 0]
            ],
            glyphs = [strokeR, [
                    [0, 0, 0, 1],
                    [0, 1, .68, 1],
                    [0, .55, .55, .55],
                    [0, 0, .68, 0]
                ],
                [
                    [.7, 1, 0, 1],
                    [0, 1, 0, 0],
                    [0, 0, .7, 0]
                ],
                [
                    [0, 0, 0, 1],
                    [0, 1, .7, 1],
                    [.7, 1, .7, 0],
                    [.7, 0, 0, 0]
                ], strokeR, [
                    [0, 0, 0, 1],
                    [0, 1, .5, 1],
                    [.5, 1, .7, .75],
                    [.7, .75, .7, .25],
                    [.7, .25, .5, 0],
                    [.5, 0, 0, 0]
                ],
                [
                    [.7, 1, .12, 1],
                    [.12, 1, 0, .72],
                    [0, .72, .7, .42],
                    [.7, .42, .58, .06],
                    [.58, .06, 0, .06]
                ]
            ],
            glyphPitch = .4,
            firstGlyphX = -(glyphs.length - 1) * glyphPitch / 2;
        glyphs.forEach((glyph, glyphIndex) => {
            const glyphX = firstGlyphX + glyphIndex * glyphPitch;
            for (const [ax, ay, bx, by] of glyph) {
                const x0 = glyphX + (ax - .35) * glyphPitch * .86,
                    y0 = centreY + (ay - .5) * glyphHeight,
                    x1 = glyphX + (bx - .35) * glyphPitch * .86,
                    y1 = centreY + (by - .5) * glyphHeight,
                    dx = x1 - x0,
                    dy = y1 - y0,
                    length = Math.hypot(dx, dy);
                if (length < .004) continue;
                const tube = new THREE.CylinderGeometry(tubeRadius, tubeRadius, length, 6);
                part(glow.neonRed, tube, (x0 + x1) / 2, (y0 + y1) / 2, z + .055, 0, 0, Math.atan2(dy, dx) - Math.PI / 2);
            }
        });
        part(paper.board, new THREE.BoxGeometry(glyphs.length * glyphPitch + .3, glyphHeight + .26, .04), 0, centreY, z + .02);
    } {
        const x = -(ROOM.w / 2 - .09),
            y = 2.15,
            z = 6.6;
        for (const [offset, height] of [
                [-.22, .34],
                [0, .44],
                [.22, .34]
            ]) part(glow.neonRed, new THREE.CylinderGeometry(.014, .014, height, 6), x, y, z + offset);
        part(paper.board, new THREE.BoxGeometry(.04, .62, .72), x - .03, y, z);
    } {
        const x = ROOM.w / 2 - .09,
            y = 2.05,
            z = -.5;
        for (let i = 0; i < 5; i++) part(glow.neonCyan, new THREE.BoxGeometry(.02, .13, .075), x, y, z - .3 + i * .15);
    } {
        part(glow.neonRed, new THREE.CylinderGeometry(.013, .013, 1.5, 6), 0, 2.3, 6.2, 0, 0, Math.PI / 2);
        part(paper.board, new THREE.BoxGeometry(1.58, .05, .12), 0, 2.3 + .07, 6.2);
        for (const side of [-1, 1]) part(paper.cord, new THREE.CylinderGeometry(.004, .004, ROOM.h - 2.3 - .09, 5), side * .68, (2.3 + .07 + ROOM.h) / 2, 6.2);
    } {
        const tiles = [];
        for (let row = 0; row < 6; row++)
            for (let column = 0; column < 6; column++) {
                const tile = new THREE.PlaneGeometry(.069, .069);
                part(tiles, tile, -1.45 + (column - 5 / 2) * .075, 2.18 - .16 + (row - 5 / 2) * .075 * .55, 2.6 + (row - 5 / 2) * .075 * .72, -.9 + (rand() - .5) * .22, (rand() - .5) * .22, (rand() - .5) * .3);
            }
        const merged = mergeGeometries(tiles, false);
        if (merged) {
            const mesh = new THREE.Mesh(merged, new THREE.MeshStandardMaterial({
                color: 15397112,
                roughness: .1,
                metalness: .5,
                emissive: 2106412,
                side: THREE.DoubleSide
            }));
            mesh.name = "mirror:groove";
            parent.add(mesh);
        }
        part(paper.board, new THREE.BoxGeometry(.5, .02, .5), -1.45, 2.18 + .03, 2.6, -.9, 0, 0);
        for (const side of [-1, 1]) part(paper.cord, new THREE.CylinderGeometry(.005, .005, ROOM.h - 2.18 - .06, 5), -1.45 + side * .2, (2.18 + ROOM.h) / 2, 2.6);
    }
}

// Spines, stacked LPs and kraft boxes on top of the wall-side shelving.
function buildRackTopClutter() {
    for (const bay of BAYS) {
        const runX = RUN_X[bay.run];
        if (Math.abs(runX) < 1.5) continue;
        const side = Math.sign(runX),
            z = bay.z;
        if (nearAlcoveOpening(side, z, .9)) continue;
        const style = zoneStyle(zoneAtZ(z)),
            buckets = bucketsFor(style),
            shelfLength = Math.min(bay.len + .2, 3.2),
            frame = frameOf(side * (ROOM.w / 2 - .17), 0, side > 0 ? -Math.PI / 2 : Math.PI / 2),
            topY = 2.556,
            runLength = shelfLength * (.34 + rand() * .22),
            runStart = z - shelfLength / 2 + .1 + rand() * (shelfLength - runLength - .2);
        for (const spine of spineRun(runLength)) addSpine(buckets, frame, runStart + runLength / 2 + spine.x, topY + .005 + spine.dy, spine.dz * .4, spine.lean, spine.t, .58, bay.g);
        const stackCount = 1 + Math.floor(rand() * 2);
        for (let i = 0; i < stackCount; i++) {
            const stackX = z - shelfLength / 2 + .22 + rand() * (shelfLength - .44),
                recordCount = 3 + Math.floor(rand() * 5);
            for (let j = 0; j < recordCount; j++) partL(buckets.wood, frame, new THREE.BoxGeometry(LP_SIZE, .008, LP_SIZE), stackX + (rand() - .5) * .02, topY + .006 + j * .0095, (rand() - .5) * .03, 0, (rand() - .5) * .28, 0);
        }
        if (rand() < .34) {
            const boxX = z - shelfLength / 2 + .3 + rand() * (shelfLength - .6);
            partL(paper.kraft, frame, new THREE.BoxGeometry(.36, .22, .3), boxX, topY + .11, 0, 0, (rand() - .5) * .25, 0);
            for (const side2 of [-1, 1]) partL(paper.kraft, frame, new THREE.BoxGeometry(.36, .17, .007), boxX, topY + .29, side2 * .15, side2 * .42, 0, 0);
        }
    }
}

// The dense side-wall poster pass: a band of overlapping posters every .24 m
// along both walls, higher where a wall shelf stands, plus a strip across the
// back wall.
function buildPosterWall() {
    const posters = posterAtlas(),
        cards = cardAtlas(),
        worldFrame = frameOf(0, 0, 0),
        hasWallShelf = (side, z) => BAYS.some(bay => {
            const runX = RUN_X[bay.run];
            if (Math.abs(runX) < 1.5 || Math.sign(runX) !== side) return false;
            return Math.abs(z - bay.z) < Math.min(bay.len + .2, 3.2) / 2 + .2;
        });
    let posterIndex = 0;
    for (const side of [-1, 1]) {
        const wallX = side * (ROOM.w / 2 - .012),
            ry = side > 0 ? -Math.PI / 2 : Math.PI / 2;
        for (let z = -ROOM.d / 2 + .55; z < ROOM.d / 2 - .95; z += .24) {
            if (nearAlcoveOpening(side, z, .8)) continue;
            const aboveShelf = hasWallShelf(side, z),
                bandBottom = aboveShelf ? 2.58 + rand() * .05 : 1.28 + rand() * .22,
                bandTop = 2.92,
                count = aboveShelf ? 2 : 5;
            for (let i = 0; i < count; i++) {
                const big = !aboveShelf && rand() < .2,
                    height = aboveShelf ? .17 + rand() * .14 : big ? .5 + rand() * .26 : .18 + rand() * .26,
                    y = bandBottom + height / 2 + rand() * Math.max(.02, bandTop - bandBottom - height),
                    layer = posterIndex % 4;
                part(paper.poster, atlasQuad(posters, posterIndex++ * 3 % POSTER_COUNT, height * .7, height), wallX - side * (.004 + layer * .0035), Math.min(y, bandTop - height / 2), z + (rand() - .5) * .24, 0, ry, (rand() - .5) * (big ? .14 : .36));
            }
            if (rand() < .55) {
                const size = .07 + rand() * .05;
                part(paper.card, atlasQuad(cards, Math.floor(rand() * CARD_COUNT), size, size * .72), wallX - side * .022, bandBottom + rand() * Math.max(.05, bandTop - bandBottom - .1), z + (rand() - .5) * .22, 0, ry, (rand() - .5) * .9);
            }
            if (!aboveShelf && rand() < .22) shopSign(worldFrame, wallX - side * .024, 1.5 + rand() * 1.1, z + (rand() - .5) * .2, ry, .22);
        }
    }
    for (let i = 0; i < 26; i++) {
        const height = .22 + rand() * .42,
            layer = i % 5;
        part(paper.poster, atlasQuad(posters, posterIndex++ % POSTER_COUNT, height * .69, height), -ROOM.w / 2 + .28 + rand() * (ROOM.w - .56), 2.18 + rand() * .72, -ROOM.d / 2 + .03 + layer * .004, 0, 0, (rand() - .5) * .3);
    }
}

// High posters near the ceiling on both side walls, density taken from the
// zone's wall recipe, plus nine across the back wall.
function buildUpperPosters() {
    const posters = posterAtlas();
    let posterIndex = 0;
    for (const side of [-1, 1]) {
        const wallX = side * (ROOM.w / 2 - .01),
            ry = side > 0 ? -Math.PI / 2 : Math.PI / 2;
        for (let i = 0; i < 12; i++) {
            const z = -ROOM.d / 2 + 1.2 + (i + .5) * ((ROOM.d - 2.4) / 12);
            if (z > 2.4 && z < 8.2 && side > 0 || z < -5.4 || nearAlcoveOpening(side, z, .8)) continue;
            const zone = zoneAtZ(z),
                density = zone ? zone.wall.density : .4;
            if (rand() > density - .05) continue;
            const height = .34 + rand() * .26,
                quad = atlasQuad(posters, posterIndex++ % POSTER_COUNT, height * .69, height),
                layer = i % 2;
            part(paper.poster, quad, wallX - side * (.004 + layer * .005), 2.66 + (rand() - .5) * .14, z, 0, ry, (rand() - .5) * .08);
        }
    }
    for (let i = 0; i < 9; i++) {
        const height = .34 + rand() * .7,
            quad = atlasQuad(posters, posterIndex++ % POSTER_COUNT, height * .68, height),
            layer = Math.floor(rand() * 5);
        part(paper.poster, quad, -ROOM.w / 2 + .3 + rand() * (ROOM.w - .6), 2.3 + rand() * .6, -ROOM.d / 2 + .02 + layer * .004, 0, 0, rand() < .7 ? (rand() - .5) * .1 : (rand() < .5 ? -1 : 1) * (.12 + rand() * .14));
    }
}

export {
    zoneAtZ,
    zoneStyle,
    BANDS,
    BAYS,
    FLICK_TABLES,
    paper,
    glow,
    paintParts,
    zoneBuckets,
    bucketsFor,
    spineRun,
    addSpine,
    buildSpineInstances,
    buildSleeveInstances,
    shopSign,
    cassetteMatrices,
    buildBay,
    buildCounter,
    nearAlcoveOpening,
    buildFloorCrates,
    buildFramedPictures,
    buildAlcove,
    buildStore
};
